import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API = '/api/crm';
const primary = '#1B6B3A';

const OUTCOMES = {
  winback: [
    { value:'reactivated', label:'✅ Reactivated', remove:false },
    { value:'will_recharge_soon', label:'🕐 Will Recharge Soon', remove:false },
    { value:'callback_requested', label:'📞 Callback Requested', remove:false },
    // price_issue removed from UI - existing data preserved in DB under lco_issue filter
    { value:'technical_issue', label:'🔧 Technical Issue', remove:false },
    { value:'lco_issue', label:'⚠️ LCO Issue', remove:false },
    { value:'no_money', label:'💸 Financial / Price Issue', remove:false },
    { value:'out_of_station', label:'✈️ Out of Station', remove:false },
    { value:'permanently_left', label:'❌ Permanently Left', remove:true },
    { value:'switched_isp', label:'🔄 Switched ISP', remove:true },
    { value:'new_kccl_connection', label:'🆕 New KCCL Connection', remove:true },
    { value:'relocated', label:'🏠 Relocated', remove:true },
    { value:'temporary_connection', label:'⏳ Temporary Connection', remove:false },
    { value:'due_to_studies', label:'📚 Due to Studies', remove:true },
  ],
  new_enquiry: [
    { value:'converted', label:'✅ Converted', remove:true },
    { value:'interested_follow_up', label:'🕐 Interested - Follow Up', remove:false },
    { value:'not_interested', label:'❌ Not Interested', remove:true },
  ],
  welcome_call: [
    { value:'satisfied', label:'✅ Satisfied', remove:false },
    { value:'has_issue', label:'⚠️ Has Issue', remove:false },
    { value:'not_reachable', label:'📵 Not Reachable', remove:false },
  ],
};

const CALL_STATUS = ['answered','not_reachable','wrong_number','busy'];

const ISP_LIST = [
  'Jio','Airtel','BSNL','Asianet','Extranet','Alliance','Kfone',
  'Not Informed','Converted KCBL Postpaid','Other ISP'
];

const SWITCH_REASONS = [
  { value:'service_issue', label:'🔧 Service Issue' },
  { value:'price_high', label:'💰 Price Too High' },
  { value:'speed_offer', label:'⚡ Better Speed Offered' },
  { value:'contract_offer', label:'📄 Contract / Offer' },
  { value:'recommendation', label:'👥 Family / Friend Recommendation' },
  { value:'other', label:'❓ Other' },
];

const SERVICE_ISSUES = [
  { value:'speed_issue', label:'Speed Issue' },
  { value:'modem_red_light', label:'Modem Red Light' },
  { value:'ping_issue', label:'PING Issue' },
  { value:'vpn_issue', label:'VPN Issue' },
  { value:'billing_issue', label:'Recharge & Billing Queries' },
  { value:'voice_complaint', label:'Voice Complaints' },
  { value:'website_issue', label:'Website Issue' },
  { value:'connectivity_issue', label:'Connectivity Issue' },
  { value:'device_issue', label:'Device Related Issue' },
  { value:'frequent_disconnection', label:'Frequent Disconnection' },
];

const LCO_COMPLAINTS = [
  { value:'payment_issue', label:'Payment Issue (overcharging)' },
  { value:'not_prompt', label:'LCO Service Not Prompt' },
  { value:'other', label:'Other LCO Issue' },
];

const STUDY_REASONS = [
  { value:'board_exam', label:'📝 Board Exam Preparation (10th/12th)' },
  { value:'entrance_exam', label:'🎯 Entrance Exam Focus (NEET/JEE/PSC)' },
  { value:'gaming_addiction', label:'🎮 Child Gaming / Social Media Addiction' },
  { value:'general_study', label:'📖 General Study Focus' },
  { value:'temporary_reconnect', label:'⏳ Temporary - Will Reconnect After Exams' },
];

const KCCL_REASONS = [
  { value:'kvbl_offer', label:'🎁 KVBL Offer / Free ONT' },
  { value:'lco_offer', label:'🤝 LCO Offer / Free ONT' },
  { value:'returned_from_isp', label:'↩️ Returned from Other ISP' },
];

const RETURN_REASONS = [
  { value:'speed_not_promised', label:'⚡ Speed Not as Promised' },
  { value:'service_issue', label:'🔧 Service Issue with Other ISP' },
  { value:'price_too_high', label:'💰 Price Too High' },
  { value:'poor_support', label:'📞 Poor Customer Support' },
  { value:'kvbl_better', label:'✅ KVBL Better in Area' },
  { value:'lco_convinced', label:'🤝 LCO Convinced to Return' },
];

const FINANCIAL_REASONS = [
  { value:'price_too_high', label:'💰 Plan Price Too High' },
  { value:'wants_cheaper_plan', label:'📉 Wants Cheaper Plan / Downgrade' },
  { value:'requesting_discount', label:'🏷️ Requesting Discount / Offer' },
  { value:'lost_job', label:'💼 Lost Job / No Income' },
  { value:'business_loss', label:'📉 Business Loss' },
  { value:'medical_emergency', label:'🏥 Medical Emergency' },
  { value:'temporary_cashflow', label:'⏳ Temporary Cash Flow Issue' },
  { value:'student_dependent', label:'🎓 Student / Dependent' },
  { value:'seasonal_issue', label:'🌾 Seasonal Issue' },
  { value:'not_willing', label:'🚫 Not Willing to Pay' },
];

const RELOCATION_TYPES = [
  { value:'kvbl_taken', label:'KVBL Connection Taken' },
  { value:'no_network', label:'No KVBL Network There' },
];
const statusColor = { pending:'#f59e0b', contacted:'#3b82f6', converted:'#10b981', removed:'#ef4444' };
const statusBg = { pending:'#fffbeb', contacted:'#eff6ff', converted:'#ecfdf5', removed:'#fef2f2' };

export default function CrmLog() {
  const { user } = useAuth();
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const isManagement = ['superadmin','isp_admin','dist_head','director'].includes(user?.role);

  const [tab, setTab] = useState(isManagement ? 'dashboard' : 'call');
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [totalLeads, setTotalLeads] = useState(0);
  const [followups, setFollowups] = useState([]);
  const [logs, setLogs] = useState([]);
  const [totalLogs, setTotalLogs] = useState(0);
  const [selectedLead, setSelectedLead] = useState(null);
  const [searchQ, setSearchQ] = useState('');
  const [searchFilter, setSearchFilter] = useState({ sub_dist:'', status:'' });
  const [leadsPage, setLeadsPage] = useState(1);
  const [logsPage, setLogsPage] = useState(1);
  const [callForm, setCallForm] = useState({ call_type:'winback', call_status:'answered', outcome:'', follow_up_date:'', remarks:'', isp_name:'', switch_reason:'', service_issue_type:'', lco_complaint_type:'', relocation_type:'', relocation_location:'', relocation_district:'', relocation_pincode:'', financial_reason:'', kccl_reason:'', kccl_returned_isp:'', kccl_return_reason:'', study_reason:'' });
  const [submitting, setSubmitting] = useState(false);
  const [submitMsg, setSubmitMsg] = useState(null);
  const [bmsData, setBmsData] = useState(null);
  const [bmsLoading, setBmsLoading] = useState(false);
  const [dupWarning, setDupWarning] = useState(null);
  const [logFilters, setLogFilters] = useState({ from:'', to:'', staff_id:'', call_type:'', outcome:'', isp_name:'', switch_reason:'', service_issue_type:'', lco_complaint_type:'', financial_reason:'', study_reason:'' });
  const [previewData, setPreviewData] = useState(null);
  const [summaryModal, setSummaryModal] = useState(false);
  const [editingLog, setEditingLog] = useState(null);
  const [editLogForm, setEditLogForm] = useState({});
  const [summaryFilter, setSummaryFilter] = useState({ from:'', to:'', sub_dist:'', staff_id:'' });
  const [summaryData, setSummaryData] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summarySubDists, setSummarySubDists] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const searchRef = useRef(null);

  useEffect(() => {
    loadStats();
    if (tab === 'followups') loadFollowups();
    if (tab === 'history') loadLogs();
  }, [tab]);

  // Auto-refresh dashboard every 2 minutes
  useEffect(() => {
    if (tab !== 'dashboard') return;
    const interval = setInterval(() => loadStats(), 120000);
    return () => clearInterval(interval);
  }, [tab]);

  const loadStats = async () => {
    try {
      const retRes = await axios.get(`${API}/retention/stats`, { headers });
      let crmData = null;
      if (isManagement) {
        const crmRes = await axios.get(`${API}/stats`, { headers });
        crmData = crmRes.data;
      }
      setStats({ ...retRes.data, crm: crmData });
    } catch(e) {}
  };
  const searchLeads = useCallback(async (q, filters, page) => {
    try {
      const params = new URLSearchParams({ q: q||'', ...filters, page, limit: 15 });
      const res = await axios.get(`${API}/retention/search?${params}`, { headers });
      setLeads(res.data.leads || []);
      setTotalLeads(res.data.total || 0);
    } catch(e) {}
  }, [token]);

  const loadFollowups = async () => {
    try {
      const res = await axios.get(`${API}/retention/followups`, { headers });
      setFollowups(res.data.leads || []);
    } catch(e) {}
  };

  const loadSummaryData = async () => {
    setSummaryLoading(true);
    setSummaryData(null);
    try {
      const params = { limit:99999, page:1 };
      if(summaryFilter.from) params.from = summaryFilter.from;
      if(summaryFilter.to) params.to = summaryFilter.to;
      if(summaryFilter.staff_id) params.staff_id = summaryFilter.staff_id;
      const r = await axios.get(API+'/logs', {headers, params});
      const logs = r.data.logs || [];
      const allSubDists = [...new Set(logs.map(l=>l.customer_sub_dist).filter(Boolean))].sort();
      setSummarySubDists(allSubDists);
      const allOutcomes = [...new Set(logs.map(l=>l.outcome).filter(Boolean))].sort();
      const pivot = {};
      logs.forEach(l => {
        const sd = summaryFilter.sub_dist ?
          (l.customer_sub_dist===summaryFilter.sub_dist ? l.customer_sub_dist : null) :
          (l.customer_sub_dist || 'Unknown');
        if(!sd) return;
        if(!pivot[sd]) pivot[sd] = { _total:0 };
        pivot[sd]._total++;
        const oc = l.outcome || 'not_applicable';
        pivot[sd][oc] = (pivot[sd][oc]||0)+1;
      });
      const OUTCOME_LABELS = {
        reactivated:'Reactivated', will_recharge_soon:'Will Recharge',
        callback_requested:'Callback', price_issue:'Price Issue',
        technical_issue:'Technical', lco_issue:'LCO Issue',
        no_money:'Financial', out_of_station:'Out of Station',
        permanently_left:'Perm Left', switched_isp:'Switched ISP',
        new_kccl_connection:'New KCCL', relocated:'Relocated',
        temporary_connection:'Temporary', due_to_studies:'Studies',
        not_applicable:'Not Reachable', converted:'Converted',
      };
      setSummaryData({ pivot, outcomes:allOutcomes, total:logs.length, OUTCOME_LABELS });
    } catch(e) { alert('Failed to load summary: '+e.message); }
    setSummaryLoading(false);
  };

  const loadLogs = async () => {
    try {
      const params = new URLSearchParams({ page: logsPage, limit: 20, ...logFilters });
      const res = await axios.get(`${API}/logs?${params}`, { headers });
      setLogs(res.data.logs || []);
      setTotalLogs(res.data.total || 0);
    } catch(e) {}
  };

  useEffect(() => {
    if (tab === 'leads') searchLeads(searchQ, searchFilter, leadsPage);
  }, [tab, searchQ, searchFilter, leadsPage]);

  useEffect(() => {
    if (tab === 'history') loadLogs();
  }, [logsPage, logFilters]);

  const selectLead = async (lead) => {
    setSelectedLead(lead);
    setCallForm({ call_type:'winback', call_status:'answered', outcome:'', follow_up_date:'', remarks:'', isp_name:'', switch_reason:'', service_issue_type:'', lco_complaint_type:'', relocation_type:'', relocation_location:'', relocation_district:'', relocation_pincode:'', financial_reason:'', kccl_reason:'', kccl_returned_isp:'', kccl_return_reason:'', study_reason:'' });
    setBmsData(null);
    setDupWarning(null);
    // Check for duplicate contact
    if (lead.last_contacted) {
      const lastDate = new Date(lead.last_contacted);
      const daysSince = Math.floor((new Date() - lastDate) / 86400000);
      if (daysSince < 7) setDupWarning({ date: lastDate.toLocaleDateString('en-IN'), days: daysSince, followup: lead.follow_up_date });
    }
    // BMS lookup
    setBmsLoading(true);
    try {
      const res = await axios.get(`${API}/bms-lookup/${lead.user_id}`, { headers, timeout: 35000 });
      if (res.data.success && res.data.session) setBmsData(res.data.session);
    } catch(e) {}
    setBmsLoading(false);
  };

  const submitCall = async () => {
    if (!selectedLead || !callForm.call_status || !callForm.outcome) {
      setSubmitMsg({ type:'error', text:'Please select call status and outcome' });
      return;
    }
    setSubmitting(true);
    try {
      // Save CRM log
      await axios.post(`${API}/log`, {
        customer_user_id: selectedLead.user_id,
        customer_name: selectedLead.name,
        customer_mobile: selectedLead.mobile,
        customer_plan: selectedLead.plan,
        customer_area: selectedLead.sub_dist_code,
        customer_lco: selectedLead.lco_name,
        customer_sub_dist: selectedLead.sub_dist_code,
        customer_lco_code: selectedLead.lco_code,
        ...callForm,
        isp_name: callForm.isp_name||'',
        switch_reason: callForm.switch_reason||'',
        service_issue_type: callForm.service_issue_type||'',
        lco_complaint_type: callForm.lco_complaint_type||'',
        relocation_type: callForm.relocation_type||'',
        relocation_location: callForm.relocation_location||'',
        relocation_district: callForm.relocation_district||'',
        relocation_pincode: callForm.relocation_pincode||''
      }, { headers });
      // Update retention lead
      axios.put(`${API}/retention/${selectedLead.user_id}`, callForm, { headers }).catch(()=>{});
      setSubmitMsg({ type:'success', text:'Call logged successfully!' });
      if (isManagement) loadStats();
      setTimeout(() => { setSelectedLead(null); setSubmitMsg(null); if (tab === 'leads') searchLeads(searchQ, searchFilter, leadsPage); }, 2000);
    } catch(e) {
      setSubmitMsg({ type:'error', text:'Failed to save. Try again.' });
    }
    setSubmitting(false);
  };

  const needsFollowup = callForm.outcome && !['permanently_left','relocated','new_kccl_connection','switched_isp','converted','reactivated','temporary_connection','out_of_station','technical_issue','lco_issue','no_money','due_to_studies'].includes(callForm.outcome);

  const KPICard = ({ label, value, color, bg, icon, onClick }) => (
    <div onClick={onClick} style={{ background:'white', borderRadius:'14px', padding:'20px', boxShadow:'0 1px 4px rgba(0,0,0,0.06)', border:'1px solid #f1f5f9', cursor: onClick ? 'pointer' : 'default', transition:'all .2s' }}
      onMouseEnter={e => { if(onClick) e.currentTarget.style.transform='translateY(-2px)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform='translateY(0)'; }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
        <div>
          <p style={{ margin:'0 0 6px', fontSize:'12px', color:'#64748b', fontWeight:'600', textTransform:'uppercase', letterSpacing:'0.5px' }}>{label}</p>
          <p style={{ margin:0, fontSize:'28px', fontWeight:'800', color:color }}>{value ?? '—'}</p>
        </div>
        <div style={{ width:'42px', height:'42px', background:bg, borderRadius:'10px', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'18px' }}>{icon}</div>
      </div>
    </div>
  );

  const subDists = ['KB13A','KB13B','KB13C','KB13D','KB13E','KB13F','KB13H','KB13J'];

  return (
    <div style={{ fontFamily:"'Segoe UI',-apple-system,sans-serif" }}>
      <style>{`
        .crm-tab { padding:10px 20px; border:none; background:transparent; cursor:pointer; font-size:14px; font-weight:500; color:#64748b; border-bottom:2px solid transparent; transition:all .2s; }
        .crm-tab.active { color:${primary}; border-bottom-color:${primary}; font-weight:700; }
        .crm-tab:hover { color:${primary}; }
        .lead-row { padding:14px 16px; border-bottom:1px solid #f8fafc; cursor:pointer; transition:background .15s; display:flex; align-items:center; gap:12px; }
        .lead-row:hover { background:#f8fafc; }
        .lead-row.selected { background:${primary}08; border-left:3px solid ${primary}; }
        .cinp { width:100%; padding:10px 14px; border:1.5px solid #e2e8f0; border-radius:8px; font-size:14px; color:#1e293b; background:white; outline:none; box-sizing:border-box; font-family:inherit; transition:border .2s; }
        .cinp:focus { border-color:${primary}; box-shadow:0 0 0 3px ${primary}15; }
        .outcome-btn { padding:9px 14px; border:1.5px solid #e2e8f0; border-radius:8px; font-size:13px; cursor:pointer; transition:all .15s; background:white; text-align:left; }
        .outcome-btn:hover { border-color:${primary}; background:${primary}08; }
        .outcome-btn.sel { border-color:${primary}; background:${primary}12; color:${primary}; font-weight:600; }
        .sbtn { padding:12px 24px; background:${primary}; color:white; border:none; border-radius:8px; font-size:14px; font-weight:700; cursor:pointer; transition:all .2s; }
        .sbtn:hover:not(:disabled) { opacity:.9; transform:translateY(-1px); }
        .sbtn:disabled { opacity:.6; cursor:not-allowed; }
      `}</style>

      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px' }}>
        <div>
          <h1 style={{ margin:'0 0 4px', fontSize:'22px', fontWeight:'800', color:'#0f172a' }}>CRM & Retention</h1>
          <p style={{ margin:0, fontSize:'13px', color:'#64748b' }}>Manage suspended customer outreach and retention campaigns</p>
        </div>
        <div style={{ display:'flex', gap:'8px' }}>
          <button onClick={() => setTab('call')} className={`crm-tab${tab==='call'?' active':''}`} style={{ border:'1px solid #e2e8f0', borderRadius:'8px', padding:'8px 16px' }}>📞 Log Call</button>
          <button onClick={() => { setTab('leads'); searchLeads(searchQ, searchFilter, 1); }} className={`crm-tab${tab==='leads'?' active':''}`} style={{ border:'1px solid #e2e8f0', borderRadius:'8px', padding:'8px 16px' }}>👥 Leads</button>
          {isManagement && <button onClick={() => setTab('dashboard')} className={`crm-tab${tab==='dashboard'?' active':''}`} style={{ border:'1px solid #e2e8f0', borderRadius:'8px', padding:'8px 16px' }}>📊 Dashboard</button>}
          <button onClick={() => setTab('followups')} className={`crm-tab${tab==='followups'?' active':''}`} style={{ border:'1px solid #e2e8f0', borderRadius:'8px', padding:'8px 16px' }}>🔔 Follow-ups {stats?.followup_today > 0 && <span style={{ background:'#ef4444', color:'white', borderRadius:'10px', padding:'1px 6px', fontSize:'11px', marginLeft:'4px' }}>{stats.followup_today}</span>}</button>
          <button onClick={() => setTab('history')} className={`crm-tab${tab==='history'?' active':''}`} style={{ border:'1px solid #e2e8f0', borderRadius:'8px', padding:'8px 16px' }}>📋 History</button>
        </div>
      </div>

      {/* DASHBOARD TAB */}
      {tab === 'dashboard' && (
        <div>
          {/* Date filter + Download */}
          <div style={{ background:'white', borderRadius:'14px', padding:'16px', marginBottom:'16px', boxShadow:'0 1px 4px rgba(0,0,0,0.06)', display:'flex', gap:'10px', alignItems:'center', flexWrap:'wrap' }}>
            <button onClick={() => loadStats()} style={{ padding:'10px 16px', background:'#f1f5f9', color:'#475569', border:'1px solid #e2e8f0', borderRadius:'8px', cursor:'pointer', fontSize:'13px', fontWeight:'600' }}>🔄 Refresh</button>
            {/* Quick date filters */}
            {[
              { label:'Today', days:0 },
              { label:'Yesterday', days:1 },
              { label:'This Week', days:7 },
              { label:'This Month', days:30 },
            ].map(({label, days}) => {
              const d = new Date();
              const today = d.toISOString().split('T')[0];
              const from = days === 0 ? today : days === 1 ?
                new Date(d-86400000).toISOString().split('T')[0] :
                new Date(d - days*86400000).toISOString().split('T')[0];
              const to = days === 1 ? from : today;
              const isActive = logFilters.from === from && logFilters.to === to;
              return <button key={label} onClick={() => setLogFilters(f=>({...f, from, to}))}
                style={{ padding:'8px 12px', background: isActive ? primary : '#f1f5f9',
                  color: isActive ? 'white' : '#475569', border: isActive ? 'none' : '1px solid #e2e8f0',
                  borderRadius:'8px', cursor:'pointer', fontSize:'12px', fontWeight:'600' }}>{label}</button>;
            })}
            <input type="date" className="cinp" style={{ width:'140px' }} value={logFilters.from} onChange={e => setLogFilters(f=>({...f, from:e.target.value}))} />
            <span style={{ color:'#94a3b8', fontSize:'13px' }}>to</span>
            <input type="date" className="cinp" style={{ width:'140px' }} value={logFilters.to} onChange={e => setLogFilters(f=>({...f, to:e.target.value}))} />
            <select className="cinp" style={{ width:'160px' }} value={logFilters.staff_id||''} onChange={e => setLogFilters(f=>({...f, staff_id:e.target.value}))}>
              <option value="">All Staff</option>
              {(stats?.crm?.byStaff||[]).map((s,i) => <option key={i} value={s._id.staff_id}>{s._id.staff_name}</option>)}
            </select>
            <select className="cinp" style={{ width:'160px' }} value={logFilters.call_type||''} onChange={e => setLogFilters(f=>({...f, call_type:e.target.value}))}>
              <option value="">All Types</option>
              <option value="winback">Retention</option>
              <option value="new_enquiry">Enquiry</option>
              <option value="welcome_call">Welcome</option>
            </select>
            <select className="cinp" style={{ width:'180px' }} value={logFilters.outcome||''} onChange={e => setLogFilters(f=>({...f, outcome:e.target.value}))}>
              <option value="">All Outcomes</option>
              <option value="reactivated">✅ Reactivated</option>
              <option value="will_recharge_soon">🕐 Will Recharge Soon</option>
              <option value="callback_requested">📞 Callback Requested</option>
              <option value="technical_issue">🔧 Technical Issue</option>
              <option value="lco_issue">⚠️ LCO Issue</option>
              <option value="no_money">💸 Financial / Price Issue</option>
              <option value="permanently_left">❌ Permanently Left</option>
              <option value="switched_isp">🔄 Switched to Other ISP</option>
              <option value="temporary_connection">⏳ Temporary Connection</option>
              <option value="new_kccl_connection">🆕 New KCCL Connection</option>
              <option value="relocated">🏠 Relocated</option>
              <option value="not_reachable">📵 Not Reachable</option>
            </select>
            <button onClick={async () => {
              try {
                const p = new URLSearchParams(logFilters);
                Object.keys(logFilters).forEach(k => { if (!logFilters[k]) p.delete(k); });
                const r = await axios.get(`${API}/stats?${p}`, {headers});
                setStats(prev=>({...prev, crm:r.data}));
              } catch(e){}
            }} style={{ padding:'10px 20px', background:primary, color:'white', border:'none', borderRadius:'8px', cursor:'pointer', fontWeight:'600', fontSize:'13px' }}>Apply</button>
            {logFilters.outcome==='switched_isp' && (
              <select className="cinp" style={{ width:'160px' }} value={logFilters.isp_name||''} onChange={e=>setLogFilters(f=>({...f,isp_name:e.target.value}))}>
                <option value=''>All ISPs</option>
                {ISP_LIST.map(i=><option key={i} value={i}>{i}</option>)}
              </select>
            )}
            {logFilters.outcome==='switched_isp' && (
              <select className="cinp" style={{ width:'160px' }} value={logFilters.switch_reason||''} onChange={e=>setLogFilters(f=>({...f,switch_reason:e.target.value}))}>
                <option value=''>All Reasons</option>
                {SWITCH_REASONS.map(r=><option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            )}
            {logFilters.outcome==='technical_issue' && (
              <select className="cinp" style={{ width:'180px' }} value={logFilters.service_issue_type||''} onChange={e=>setLogFilters(f=>({...f,service_issue_type:e.target.value}))}>
                <option value=''>All Issue Types</option>
                {SERVICE_ISSUES.map(s=><option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            )}
            {logFilters.outcome==='lco_issue' && (
              <select className="cinp" style={{ width:'180px' }} value={logFilters.lco_complaint_type||''} onChange={e=>setLogFilters(f=>({...f,lco_complaint_type:e.target.value}))}>
                <option value=''>All LCO Complaints</option>
                {LCO_COMPLAINTS.map(s=><option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            )}
            {logFilters.outcome==='no_money' && (
              <select className="cinp" style={{ width:'180px' }} value={logFilters.financial_reason||''} onChange={e=>setLogFilters(f=>({...f,financial_reason:e.target.value}))}>
                <option value=''>All Financial Reasons</option>
                {FINANCIAL_REASONS.map(s=><option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            )}
            {logFilters.outcome==='due_to_studies' && (
              <select className="cinp" style={{ width:'180px' }} value={logFilters.study_reason||''} onChange={e=>setLogFilters(f=>({...f,study_reason:e.target.value}))}>
                <option value=''>All Study Reasons</option>
                {STUDY_REASONS.map(s=><option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            )}
            <button onClick={() => setLogFilters({from:'',to:'',staff_id:'',call_type:'',outcome:'',isp_name:'',switch_reason:'',service_issue_type:'',lco_complaint_type:'',financial_reason:'',study_reason:''})}
              style={{ padding:'10px 16px', background:'#f1f5f9', color:'#475569', border:'1px solid #e2e8f0', borderRadius:'8px', cursor:'pointer', fontSize:'13px' }}>Clear</button>
            {['director','dist_admin','superadmin','isp_admin','dist_head'].includes(user?.role) && <button onClick={async () => {
              try {
                const dlParams = {};
                if (logFilters.from) dlParams.from = logFilters.from;
                if (logFilters.to) dlParams.to = logFilters.to;
                if (logFilters.staff_id) dlParams.staff_id = logFilters.staff_id;
                if (logFilters.call_type) dlParams.call_type = logFilters.call_type;
                if (logFilters.outcome) dlParams.outcome = logFilters.outcome;
                dlParams.limit = 99999;
                dlParams.page = 1;
                const r = await axios.get(`${API}/logs`, {headers, params:dlParams});
                const logs = r.data.logs || [];
                const rows = [['Date','Agent','Customer','User ID','Mobile','Sub-Dist','LCO','Call Type','Status','Outcome','Remarks']];
                logs.forEach(l => rows.push([
                  new Date(l.createdAt).toLocaleDateString('en-IN'),
                  l.staff_name||'', l.customer_name||'', l.customer_user_id||'',
                  l.customer_mobile||'', l.customer_sub_dist||'', l.customer_lco||'',
                  l.call_type||'', l.call_status||'', l.outcome||'', l.remarks||''
                ]));
                const csv = rows.map(r => r.map(c => '"'+String(c).replace(/"/g,'""')+'"').join(',')).join('\n');
                const a = document.createElement('a');
                a.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
                a.download = 'CRM_Report_' + new Date().toISOString().split('T')[0] + '.csv';
                a.click();
              } catch(e) { alert('Download failed'); }
            }} style={{ padding:'10px 20px', background:'#f1f5f9', color:'#475569', border:'1px solid #e2e8f0', borderRadius:'8px', cursor:'pointer', fontWeight:'600', fontSize:'13px' }}>
              📥 Download CSV
            </button>}
            {['director','dist_admin','superadmin','isp_admin','dist_head'].includes(user && user.role) && <button onClick={()=>{setSummaryModal(true);setSummaryData(null);setSummarySubDists([]);}}
              style={{ padding:'10px 20px', background:'#7c3aed', color:'white', border:'none', borderRadius:'8px', cursor:'pointer', fontWeight:'600', fontSize:'13px' }}>
              📊 Summary Report
            </button>}
            {['director','dist_admin','superadmin','isp_admin','dist_head'].includes(user?.role) && <button onClick={async () => {
              try {
                setPreviewLoading(true);
                setPreviewData(null);
                const params = {};
                if (logFilters.from) params.from = logFilters.from;
                if (logFilters.to) params.to = logFilters.to;
                if (logFilters.staff_id) params.staff_id = logFilters.staff_id;
                if (logFilters.call_type) params.call_type = logFilters.call_type;
                if (logFilters.outcome) params.outcome = logFilters.outcome;
                if (logFilters.isp_name) params.isp_name = logFilters.isp_name;
                if (logFilters.switch_reason) params.switch_reason = logFilters.switch_reason;
                params.limit = 99999;
                params.page = 1;
                const r = await axios.get(`${API}/logs`, {headers, params});
                setPreviewData(r.data.logs || []);
              } catch(e) { alert('Preview failed'); }
              finally { setPreviewLoading(false); }
            }} style={{ padding:'10px 20px', background:'#6366f1', color:'white', border:'none', borderRadius:'8px', cursor:'pointer', fontWeight:'600', fontSize:'13px' }}>
              {previewLoading ? '⏳ Loading...' : '👁️ Preview Report'}
            </button>}
          </div>

          {summaryModal && (
            <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.6)',zIndex:2000,display:'flex',alignItems:'flex-start',justifyContent:'center',padding:'20px',overflowY:'auto'}}>
              <div style={{background:'white',borderRadius:'16px',width:'100%',maxWidth:'1200px',marginTop:'10px',boxShadow:'0 20px 60px rgba(0,0,0,0.3)'}}>
                <div style={{padding:'20px 24px',borderBottom:'1px solid #e2e8f0',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <div style={{fontSize:'18px',fontWeight:'700',color:'#0f172a'}}>📊 CRM Summary Report</div>
                  <button onClick={()=>setSummaryModal(false)} style={{padding:'8px 16px',background:'#f1f5f9',border:'1px solid #e2e8f0',borderRadius:'8px',cursor:'pointer'}}>✕ Close</button>
                </div>
                <div style={{padding:'16px 24px',background:'#f8fafc',borderBottom:'1px solid #e2e8f0',display:'flex',flexWrap:'wrap',gap:'10px',alignItems:'flex-end'}}>
                  <div>
                    <label style={{display:'block',fontSize:'11px',fontWeight:'700',color:'#475569',marginBottom:'4px'}}>From</label>
                    <input type="date" value={summaryFilter.from} onChange={e=>setSummaryFilter(f=>({...f,from:e.target.value}))} className="cinp" style={{width:'140px'}} />
                  </div>
                  <div>
                    <label style={{display:'block',fontSize:'11px',fontWeight:'700',color:'#475569',marginBottom:'4px'}}>To</label>
                    <input type="date" value={summaryFilter.to} onChange={e=>setSummaryFilter(f=>({...f,to:e.target.value}))} className="cinp" style={{width:'140px'}} />
                  </div>
                  <div style={{display:'flex',gap:'6px'}}>
                    {[['Today',0],['Week',7],['Month',30]].map(([lbl,days])=>(
                      <button key={lbl} onClick={()=>{
                        const to=new Date().toISOString().split('T')[0];
                        const from=days===0?to:new Date(Date.now()-days*86400000).toISOString().split('T')[0];
                        setSummaryFilter(f=>({...f,from,to}));
                      }} style={{padding:'8px 12px',background:'#e2e8f0',border:'none',borderRadius:'6px',cursor:'pointer',fontSize:'12px',fontWeight:'600'}}>{lbl}</button>
                    ))}
                  </div>
                  <div>
                    <label style={{display:'block',fontSize:'11px',fontWeight:'700',color:'#475569',marginBottom:'4px'}}>Sub-Dist</label>
                    <select value={summaryFilter.sub_dist} onChange={e=>setSummaryFilter(f=>({...f,sub_dist:e.target.value}))} className="cinp" style={{width:'160px'}}>
                      <option value=''>All Sub-Dists</option>
                      {summarySubDists.map(s=><option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{display:'block',fontSize:'11px',fontWeight:'700',color:'#475569',marginBottom:'4px'}}>Staff</label>
                    <select value={summaryFilter.staff_id} onChange={e=>setSummaryFilter(f=>({...f,staff_id:e.target.value}))} className="cinp" style={{width:'160px'}}>
                      <option value=''>All Staff</option>
                      {(stats && stats.crm && stats.crm.byStaff ? stats.crm.byStaff : []).map((s,i)=>(
                        <option key={i} value={s._id ? s._id.staff_id : ''}>{s._id ? s._id.staff_name : ''}</option>
                      ))}
                    </select>
                  </div>
                  <button onClick={loadSummaryData} disabled={summaryLoading} style={{padding:'8px 20px',background:'#1B6B3A',color:'white',border:'none',borderRadius:'6px',cursor:'pointer',fontWeight:'600',fontSize:'13px'}}>
                    {summaryLoading ? 'Loading...' : 'Generate'}
                  </button>
                </div>
                <div style={{padding:'24px',overflowX:'auto'}}>
                  {!summaryData ? (
                    <div style={{textAlign:'center',padding:'60px',color:'#94a3b8'}}>
                      <div style={{fontSize:'40px',marginBottom:'12px'}}>📊</div>
                      <div style={{fontWeight:'600',fontSize:'15px'}}>Set filters and click Generate</div>
                    </div>
                  ) : (
                    <div>
                      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'10px',marginBottom:'20px'}}>
                        {[
                          {label:'Total Calls',value:summaryData.total,color:'#1B6B3A'},
                          {label:'Sub-Dists',value:Object.keys(summaryData.pivot).length,color:'#10b981'},
                          {label:'Reactivated',value:Object.values(summaryData.pivot).reduce((a,p)=>a+(p.reactivated||0),0),color:'#6366f1'},
                          {label:'Lost',value:Object.values(summaryData.pivot).reduce((a,p)=>a+(p.switched_isp||0)+(p.permanently_left||0)+(p.relocated||0),0),color:'#ef4444'},
                        ].map(card=>(
                          <div key={card.label} style={{background:card.color,borderRadius:'10px',padding:'14px',textAlign:'center',color:'white'}}>
                            <div style={{fontSize:'26px',fontWeight:'800'}}>{card.value}</div>
                            <div style={{fontSize:'11px',opacity:0.9,marginTop:'4px'}}>{card.label}</div>
                          </div>
                        ))}
                      </div>
                      <div style={{overflowX:'auto'}}>
                        <table style={{width:'100%',borderCollapse:'collapse',fontSize:'12px'}}>
                          <thead>
                            <tr style={{background:'#1B6B3A',color:'white'}}>
                              <th style={{padding:'10px 12px',textAlign:'left',position:'sticky',left:0,background:'#1B6B3A',zIndex:2,minWidth:'120px'}}>Sub-Dist</th>
                              <th style={{padding:'10px 12px',textAlign:'center',minWidth:'70px'}}>Total</th>
                              {summaryData.outcomes.map(oc=>(
                                <th key={oc} style={{padding:'10px 8px',textAlign:'center',whiteSpace:'nowrap',fontSize:'11px',minWidth:'90px'}}>
                                  {summaryData.OUTCOME_LABELS[oc]||oc.replace(/_/g,' ')}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(summaryData.pivot).sort((a,b)=>b[1]._total-a[1]._total).map(([sd,data],i)=>(
                              <tr key={sd} style={{borderBottom:'1px solid #f1f5f9',background:i%2===0?'white':'#fafafa'}}>
                                <td style={{padding:'10px 12px',fontWeight:'700',color:'#1B6B3A',position:'sticky',left:0,background:i%2===0?'white':'#fafafa',zIndex:1}}>{sd}</td>
                                <td style={{padding:'10px 12px',textAlign:'center',fontWeight:'800',color:'#0f172a'}}>{data._total}</td>
                                {summaryData.outcomes.map(oc=>(
                                  <td key={oc} style={{padding:'10px 8px',textAlign:'center',color:data[oc]?'#0f172a':'#cbd5e1'}}>
                                    {data[oc]||'-'}
                                  </td>
                                ))}
                              </tr>
                            ))}
                            <tr style={{background:'#1B6B3A',color:'white',fontWeight:'800'}}>
                              <td style={{padding:'10px 12px',position:'sticky',left:0,background:'#1B6B3A',zIndex:1}}>GRAND TOTAL</td>
                              <td style={{padding:'10px 12px',textAlign:'center'}}>{summaryData.total}</td>
                              {summaryData.outcomes.map(oc=>(
                                <td key={oc} style={{padding:'10px 8px',textAlign:'center'}}>
                                  {Object.values(summaryData.pivot).reduce((a,p)=>a+(p[oc]||0),0)||'-'}
                                </td>
                              ))}
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      <div style={{marginTop:'16px',textAlign:'right'}}>
                        <button onClick={()=>{
                          const oc=summaryData.outcomes;
                          const ol=summaryData.OUTCOME_LABELS;
                          const rows=[['Sub-Dist','Total',...oc.map(o=>ol[o]||o)]];
                          Object.entries(summaryData.pivot).sort((a,b)=>b[1]._total-a[1]._total).forEach(([sd,data])=>{
                            rows.push([sd,data._total,...oc.map(o=>data[o]||0)]);
                          });
                          rows.push(['GRAND TOTAL',summaryData.total,...oc.map(o=>Object.values(summaryData.pivot).reduce((a,p)=>a+(p[o]||0),0))]);
                          const csv=rows.map(r=>r.map(c=>'"'+String(c).replace(/"/g,'""')+'"').join(',')).join('\n');
                          const a=document.createElement('a');
                          a.href='data:text/csv;charset=utf-8,'+encodeURIComponent(csv);
                          a.download='CRM_Summary_'+new Date().toISOString().split('T')[0]+'.csv';
                          a.click();
                        }} style={{padding:'10px 24px',background:'#10b981',color:'white',border:'none',borderRadius:'8px',cursor:'pointer',fontWeight:'700',fontSize:'13px'}}>
                          Download CSV
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          {/* Preview Modal */}
          {previewData && (
            <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:'20px' }}>
              <div style={{ background:'white', borderRadius:'16px', width:'100%', maxWidth:'1100px', height:'90vh', display:'flex', flexDirection:'column', overflow:'hidden' }}>
                {/* Modal Header */}
                <div style={{ padding:'16px 20px', borderBottom:'1px solid #e2e8f0', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <div>
                    <div style={{ fontWeight:'700', fontSize:'16px', color:'#1e293b' }}>📊 CRM Report Preview</div>
                    <div style={{ fontSize:'12px', color:'#64748b', marginTop:'2px' }}>
                      {previewData.length} records
                      {logFilters.from && ` · From ${logFilters.from}`}
                      {logFilters.to && ` to ${logFilters.to}`}
                      {logFilters.staff_id && ` · Staff filtered`}
                      {logFilters.outcome && ` · Outcome: ${logFilters.outcome.replace(/_/g,' ')}`}
                    </div>
                  </div>
                  <div style={{ display:'flex', gap:'8px' }}>
                    <button onClick={async () => {
                      const rows = [['Date','Time','Agent','Customer','User ID','Mobile','Sub-Dist','LCO','Call Type','Status','Outcome','Remarks']];
                      previewData.forEach(l => rows.push([
                        new Date(l.createdAt).toLocaleDateString('en-IN'),
                        new Date(l.createdAt).toLocaleTimeString('en-IN'),
                        l.staff_name||'', l.customer_name||'', l.customer_user_id||'',
                        l.customer_mobile||'', l.customer_sub_dist||'', l.customer_lco||'',
                        l.call_type||'', l.call_status||'', l.outcome||'', l.remarks||''
                      ]));
                      const csv = rows.map(r => r.map(c => '"'+String(c).replace(/"/g,'""')+'"').join(',')).join('\n');
                      const a = document.createElement('a');
                      a.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
                      a.download = 'CRM_Report_' + new Date().toISOString().split('T')[0] + '.csv';
                      a.click();
                    }} style={{ padding:'8px 16px', background:'#f1f5f9', color:'#475569', border:'1px solid #e2e8f0', borderRadius:'8px', cursor:'pointer', fontWeight:'600', fontSize:'13px' }}>
                      📥 Download
                    </button>
                    <button onClick={() => setPreviewData(null)} style={{ padding:'8px 16px', background:'#ef4444', color:'white', border:'none', borderRadius:'8px', cursor:'pointer', fontWeight:'600', fontSize:'13px' }}>✕ Close</button>
                  </div>
                </div>
                {/* Summary bar */}
                <div style={{ padding:'10px 20px', background:'#f8fafc', borderBottom:'1px solid #e2e8f0', display:'flex', gap:'20px', flexWrap:'wrap' }}>
                  {[
                    {label:'Total', value:previewData.length, color:'#6366f1'},
                    {label:'Answered', value:previewData.filter(l=>l.call_status==='answered').length, color:'#10b981'},
                    {label:'Not Reachable', value:previewData.filter(l=>l.call_status==='not_reachable').length, color:'#ef4444'},
                    {label:'Reactivated', value:previewData.filter(l=>l.outcome==='reactivated').length, color:'#6366f1'},
                    {label:'Will Recharge', value:previewData.filter(l=>l.outcome==='will_recharge_soon').length, color:'#f59e0b'},
                    {label:'Permanently Left', value:previewData.filter(l=>l.outcome==='permanently_left').length, color:'#94a3b8'},
                  ].map((s,i) => (
                    <div key={i} style={{ fontSize:'13px' }}>
                      <span style={{ color:'#64748b' }}>{s.label}: </span>
                      <span style={{ fontWeight:'700', color:s.color }}>{s.value}</span>
                    </div>
                  ))}
                </div>
                {/* ISP Summary Cards - only for switched_isp */}
                {logFilters.outcome==='switched_isp' && previewData.length>0 && (
                  <div style={{ padding:'10px 20px', background:'#fff', borderBottom:'1px solid #e2e8f0', display:'flex', gap:'8px', flexWrap:'wrap' }}>
                    {['Jio','Airtel','BSNL','Asianet','Extranet','Alliance','Kfone','Not Informed','Converted KCBL Postpaid','Other ISP'].map(isp=>{
                      const count=previewData.filter(l=>l.isp_name===isp).length;
                      if(!count) return null;
                      return <div key={isp} style={{ background:'#fee2e2', color:'#991b1b', padding:'4px 12px', borderRadius:'20px', fontSize:'12px', fontWeight:'700' }}>{isp}: {count}</div>;
                    })}
                    <div style={{ background:'#1B6B3A', color:'white', padding:'4px 12px', borderRadius:'20px', fontSize:'12px', fontWeight:'700' }}>Total: {previewData.length}</div>
                  </div>
                )}
                {/* Table */}
                <div style={{ overflowY:'auto', flex:1, minHeight:0 }}>
                  <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'12px' }}>
                    <thead style={{ position:'sticky', top:0, background:'#f8fafc', zIndex:1 }}>
                      <tr>
                        {['#','Date','Time','Agent','Customer','User ID','Sub-Dist','LCO','Type','Status','Outcome',...(logFilters.outcome==='switched_isp'?['ISP','Reason']:logFilters.outcome==='technical_issue'?['Issue Type']:logFilters.outcome==='lco_issue'?['LCO Complaint']:logFilters.outcome==='no_money'?['Financial Reason']:logFilters.outcome==='due_to_studies'?['Study Reason']:logFilters.outcome==='relocated'?['Relocation','Location']:[]),'Remarks'].map((h,i) => (
                          <th key={i} style={{ padding:'10px 12px', textAlign:'left', fontWeight:'700', color:'#475569', borderBottom:'2px solid #e2e8f0', whiteSpace:'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.map((l,i) => (
                        <tr key={i} style={{ borderBottom:'1px solid #f1f5f9', background:i%2===0?'white':'#fafafa' }}>
                          <td style={{ padding:'8px 12px', color:'#94a3b8' }}>{i+1}</td>
                          <td style={{ padding:'8px 12px', whiteSpace:'nowrap' }}>{new Date(l.createdAt).toLocaleDateString('en-IN')}</td>
                          <td style={{ padding:'8px 12px', whiteSpace:'nowrap', color:'#64748b' }}>{new Date(l.createdAt).toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit'})}</td>
                          <td style={{ padding:'8px 12px', fontWeight:'600', color:'#1e293b' }}>{l.staff_name||'-'}</td>
                          <td style={{ padding:'8px 12px', maxWidth:'120px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{l.customer_name||'-'}</td>
                          <td style={{ padding:'8px 12px', fontFamily:'monospace', color:'#6366f1' }}>{l.customer_user_id||'-'}</td>
                          <td style={{ padding:'8px 12px', color:'#64748b' }}>{l.customer_sub_dist||'-'}</td>
                          <td style={{ padding:'8px 12px', color:'#64748b' }}>{l.customer_lco||'-'}</td>
                          <td style={{ padding:'8px 12px' }}>{l.call_type==='winback'?'Retention':l.call_type==='new_enquiry'?'Enquiry':'Welcome'}</td>
                          <td style={{ padding:'8px 12px' }}>
                            <span style={{ padding:'2px 8px', borderRadius:'10px', fontSize:'11px', fontWeight:'600',
                              background:l.call_status==='answered'?'#dcfce7':l.call_status==='not_reachable'?'#fee2e2':'#f1f5f9',
                              color:l.call_status==='answered'?'#166534':l.call_status==='not_reachable'?'#991b1b':'#475569'
                            }}>{l.call_status||'-'}</span>
                          </td>
                          <td style={{ padding:'8px 12px' }}>
                            <span style={{ padding:'2px 8px', borderRadius:'10px', fontSize:'11px', fontWeight:'600',
                              background:l.outcome==='reactivated'?'#dbeafe':l.outcome==='permanently_left'?'#fee2e2':l.outcome==='will_recharge_soon'?'#fef3c7':'#f1f5f9',
                              color:l.outcome==='reactivated'?'#1d4ed8':l.outcome==='permanently_left'?'#991b1b':l.outcome==='will_recharge_soon'?'#92400e':'#475569'
                            }}>{(l.outcome||'-').replace(/_/g,' ').replace('switched isp','switched to other isp')}</span>
                          </td>
                          {logFilters.outcome==='switched_isp' && <>
                            <td style={{ padding:'8px 12px' }}><span style={{ background:'#fee2e2', color:'#991b1b', padding:'2px 8px', borderRadius:'10px', fontSize:'11px', fontWeight:'600' }}>{l.isp_name||'-'}</span></td>
                            <td style={{ padding:'8px 12px', color:'#475569' }}>{(l.switch_reason||'-').replace(/_/g,' ')}</td>
                          </>}
                          {logFilters.outcome==='technical_issue' && <td style={{ padding:'8px 12px', color:'#475569' }}>{(l.service_issue_type||'-').replace(/_/g,' ')}</td>}
                          {logFilters.outcome==='lco_issue' && <td style={{ padding:'8px 12px', color:'#475569' }}>{(l.lco_complaint_type||'-').replace(/_/g,' ')}</td>}
                          {logFilters.outcome==='no_money' && <td style={{ padding:'8px 12px', color:'#475569' }}>{(l.financial_reason||'-').replace(/_/g,' ')}</td>}
                          {logFilters.outcome==='due_to_studies' && <td style={{ padding:'8px 12px', color:'#475569' }}>{(l.study_reason||'-').replace(/_/g,' ')}</td>}
                          {logFilters.outcome==='relocated' && <>
                            <td style={{ padding:'8px 12px', color:'#475569' }}>{(l.relocation_type||'-').replace(/_/g,' ')}</td>
                            <td style={{ padding:'8px 12px', color:'#475569' }}>{l.relocation_location||''}{l.relocation_district?' / '+l.relocation_district:''}{l.relocation_pincode?' - '+l.relocation_pincode:''}</td>
                          </>}
                          <td style={{ padding:'8px 12px', color:'#64748b', maxWidth:'200px', wordBreak:'break-word', whiteSpace:'pre-wrap' }}>{l.remarks||'-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* KPI Cards */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'14px', marginBottom:'16px' }}>
            <KPICard label="Total Calls (Filtered)" value={stats?.crm?.total?.toLocaleString()} color="#6366f1" bg="#eef2ff" icon="📊" />
            <KPICard label="Calls Today" value={stats?.crm?.today} color={primary} bg={`${primary}10`} icon="📞" />
            <KPICard label="Follow-ups Today" value={stats?.followup_today} color="#8b5cf6" bg="#f5f3ff" icon="🔔" onClick={() => setTab('followups')} />
            <KPICard label="Converted Leads" value={stats?.converted?.toLocaleString()} color="#10b981" bg="#ecfdf5" icon="✅" />
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'16px', marginBottom:'16px' }}>
            {/* Staff Performance */}
            <div style={{ background:'white', borderRadius:'14px', padding:'24px', boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
              <h3 style={{ margin:'0 0 16px', fontSize:'15px', fontWeight:'700', color:'#0f172a' }}>👥 Staff Performance</h3>
              {(stats?.crm?.byStaff||[]).length === 0 ? (
                <p style={{ color:'#94a3b8', fontSize:'13px' }}>No data yet</p>
              ) : (stats?.crm?.byStaff||[]).map((s,i) => (
                <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 0', borderBottom:'1px solid #f8fafc' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                    <div style={{ width:'32px', height:'32px', background:`${primary}15`, borderRadius:'8px', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'14px' }}>👤</div>
                    <span style={{ fontSize:'13px', fontWeight:'600', color:'#1e293b' }}>{s._id.staff_name}</span>
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                    <div style={{ width:'80px', height:'6px', background:'#f1f5f9', borderRadius:'3px', overflow:'hidden' }}>
                      <div style={{ height:'100%', width:`${Math.min((s.count/(stats?.crm?.total||1))*100,100)}%`, background:primary, borderRadius:'3px' }} />
                    </div>
                    <span style={{ fontSize:'13px', fontWeight:'700', color:primary, minWidth:'30px', textAlign:'right' }}>{s.count}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Outcome Breakdown */}
            <div style={{ background:'white', borderRadius:'14px', padding:'24px', boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
              <h3 style={{ margin:'0 0 16px', fontSize:'15px', fontWeight:'700', color:'#0f172a' }}>📋 Outcome Breakdown</h3>
              {(stats?.crm?.byOutcome||[]).filter(o=>o._id&&o._id!=='not_applicable').map((o,i) => {
                const colors = ['#10b981','#f59e0b','#ef4444','#6366f1','#3b82f6','#8b5cf6','#ec4899'];
                return (
                  <div key={i} style={{ marginBottom:'10px' }}>
                    <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'4px' }}>
                      <span style={{ fontSize:'12px', color:'#475569' }}>{o._id.replace(/_/g,' ')}</span>
                      <span style={{ fontSize:'12px', fontWeight:'700', color:colors[i%colors.length] }}>{o.count}</span>
                    </div>
                    <div style={{ height:'6px', background:'#f1f5f9', borderRadius:'3px' }}>
                      <div style={{ height:'100%', width:`${(o.count/Math.max(...(stats?.crm?.byOutcome||[]).map(x=>x.count),1))*100}%`, background:colors[i%colors.length], borderRadius:'3px' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Retention Leads Summary */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:'14px', marginBottom:'16px' }}>
            <KPICard label="Suspended List Total" value={stats?.total?.toLocaleString()} color="#6366f1" bg="#eef2ff" icon="👥" />
            <KPICard label="Not Yet Called" value={stats?.pending?.toLocaleString()} color="#f59e0b" bg="#fffbeb" icon="⏳" onClick={() => { setTab('leads'); setSearchFilter({status:'pending', sub_dist:''}); }} />
            <KPICard label="Already Contacted" value={stats?.contacted?.toLocaleString()} color="#3b82f6" bg="#eff6ff" icon="📱" onClick={() => { setTab('leads'); setSearchFilter({status:'contacted', sub_dist:''}); }} />
            <KPICard label="Dropped / Left" value={stats?.removed?.toLocaleString()} color="#ef4444" bg="#fef2f2" icon="❌" />
            <KPICard label="Reactivated" value={stats?.converted?.toLocaleString()} color="#10b981" bg="#ecfdf5" icon="✅" />
          </div>

          {/* Sub-dist breakdown */}
          <div style={{ background:'white', borderRadius:'14px', padding:'24px', boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
            <h3 style={{ margin:'0 0 16px', fontSize:'15px', fontWeight:'700', color:'#0f172a' }}>📍 Leads by Sub-Distribution</h3>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'10px' }}>
              {subDists.map(sd => (
                <div key={sd} onClick={() => { setTab('leads'); setSearchFilter({sub_dist:sd, status:''}); }}
                  style={{ padding:'14px 16px', border:'1px solid #e2e8f0', borderRadius:'10px', cursor:'pointer', transition:'all .15s' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor=primary; e.currentTarget.style.background=primary+'08'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor='#e2e8f0'; e.currentTarget.style.background='white'; }}>
                  <p style={{ margin:'0 0 2px', fontSize:'13px', fontWeight:'700', color:'#0f172a' }}>{sd}</p>
                  <p style={{ margin:0, fontSize:'11px', color:'#94a3b8' }}>Click to view leads</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* LEADS TAB */}
      {tab === 'leads' && (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 420px', gap:'16px', alignItems:'start' }}>
          {/* Left - Lead List */}
          <div style={{ background:'white', borderRadius:'14px', boxShadow:'0 1px 4px rgba(0,0,0,0.06)', overflow:'hidden' }}>
            {/* Search bar */}
            <div style={{ padding:'16px', borderBottom:'1px solid #f1f5f9', display:'flex', gap:'10px' }}>
              <input className="cinp" style={{ flex:1 }} placeholder="🔍 Search by name, mobile, user ID..."
                value={searchQ} onChange={e => { setSearchQ(e.target.value); setLeadsPage(1); searchLeads(e.target.value, searchFilter, 1); }} />
              <select className="cinp" style={{ width:'130px' }} value={searchFilter.sub_dist}
                onChange={e => { setSearchFilter({...searchFilter, sub_dist:e.target.value}); searchLeads(searchQ, {...searchFilter, sub_dist:e.target.value}, 1); }}>
                <option value="">All Sub-Dist</option>
                {subDists.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <select className="cinp" style={{ width:'130px' }} value={searchFilter.status}
                onChange={e => { setSearchFilter({...searchFilter, status:e.target.value}); searchLeads(searchQ, {...searchFilter, status:e.target.value}, 1); }}>
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="contacted">Contacted</option>
                <option value="converted">Converted</option>
              </select>
            </div>
            <div style={{ padding:'10px 16px', background:'#f8fafc', fontSize:'12px', color:'#64748b' }}>
              {totalLeads.toLocaleString()} leads found
            </div>
            {/* Lead rows */}
            <div style={{ maxHeight:'calc(100vh - 280px)', overflowY:'auto' }}>
              {leads.length === 0 ? (
                <div style={{ padding:'40px', textAlign:'center', color:'#94a3b8' }}>
                  <div style={{ fontSize:'40px', marginBottom:'8px' }}>🔍</div>
                  <p>Search for leads above</p>
                </div>
              ) : leads.map(lead => (
                <div key={lead._id} className={`lead-row${selectedLead?._id === lead._id ? ' selected' : ''}`}
                  onClick={() => selectLead(lead)}>
                  <div style={{ width:'40px', height:'40px', background:`${primary}15`, borderRadius:'10px', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'16px', flexShrink:0 }}>
                    {lead.crm_status === 'converted' ? '✅' : lead.crm_status === 'removed' ? '❌' : lead.follow_up_date ? '🔔' : '👤'}
                  </div>
                  <div style={{ flex:1, overflow:'hidden' }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                      <p style={{ margin:'0 0 2px', fontSize:'13px', fontWeight:'700', color:'#1e293b', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{lead.name}</p>
                      <span style={{ background:statusBg[lead.crm_status], color:statusColor[lead.crm_status], padding:'2px 8px', borderRadius:'10px', fontSize:'10px', fontWeight:'700', flexShrink:0, marginLeft:'8px' }}>
                        {lead.crm_status?.toUpperCase()}
                      </span>
                    </div>
                    <p style={{ margin:'0 0 2px', fontSize:'12px', color:'#64748b' }}>{lead.mobile} · {lead.user_id}</p>
                    <p style={{ margin:0, fontSize:'11px', color:'#94a3b8' }}>{lead.sub_dist_code} · {lead.plan?.substring(0,20)}</p>
                  </div>
                </div>
              ))}
            </div>
            {/* Pagination */}
            <div style={{ padding:'12px 16px', borderTop:'1px solid #f1f5f9', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <button disabled={leadsPage===1} onClick={() => setLeadsPage(p=>p-1)} style={{ padding:'6px 14px', border:'1px solid #e2e8f0', borderRadius:'6px', cursor:'pointer', fontSize:'13px', background: leadsPage===1?'#f8fafc':'white' }}>← Prev</button>
              <span style={{ fontSize:'12px', color:'#64748b' }}>Page {leadsPage}</span>
              <button onClick={() => setLeadsPage(p=>p+1)} style={{ padding:'6px 14px', border:'1px solid #e2e8f0', borderRadius:'6px', cursor:'pointer', fontSize:'13px' }}>Next →</button>
            </div>
          </div>

          {/* Right - Call Panel */}
          <div>
            {!selectedLead ? (
              <div style={{ background:'white', borderRadius:'14px', padding:'40px', textAlign:'center', boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
                <div style={{ fontSize:'48px', marginBottom:'12px' }}>👈</div>
                <p style={{ color:'#64748b', fontSize:'14px' }}>Select a lead from the list to log a call</p>
              </div>
            ) : (
              <div style={{ background:'white', borderRadius:'14px', boxShadow:'0 1px 4px rgba(0,0,0,0.06)', overflow:'hidden' }}>
                {/* Customer header */}
                <div style={{ padding:'20px', background:`${primary}08`, borderBottom:'1px solid #f1f5f9' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                    <div>
                      <h3 style={{ margin:'0 0 4px', fontSize:'16px', fontWeight:'800', color:'#0f172a' }}>{selectedLead.name}</h3>
                      <p style={{ margin:'0 0 2px', fontSize:'13px', color:'#64748b' }}>📱 {selectedLead.mobile} · 🆔 {selectedLead.user_id}</p>
                      <p style={{ margin:0, fontSize:'12px', color:'#94a3b8' }}>📋 {selectedLead.plan} · 🏢 {selectedLead.sub_dist_code}</p>
                    </div>
                    <button onClick={() => setSelectedLead(null)} style={{ background:'none', border:'none', cursor:'pointer', color:'#94a3b8', fontSize:'18px' }}>✕</button>
                  </div>
                  {/* BMS status */}
                  {bmsLoading && <div style={{ marginTop:'10px', fontSize:'12px', color:'#f59e0b' }}>⏳ Checking live status...</div>}
                  {bmsData && (
                    <div style={{ marginTop:'10px', padding:'10px 12px', background:'white', borderRadius:'8px', fontSize:'12px' }}>
                      <span style={{ fontWeight:'700', color: bmsData.status==='Suspended'?'#ef4444':bmsData.online?'#10b981':'#f59e0b' }}>
                        {bmsData.status==='Suspended'?'🔴 Suspended':bmsData.online?'🟢 Online':'🟡 Offline'}
                      </span>
                      {bmsData.expiry && <span style={{ color:'#64748b', marginLeft:'8px' }}>Exp: {bmsData.expiry}</span>}
                    </div>
                  )}
                </div>

                {/* Duplicate warning */}
                {dupWarning && (
                  <div style={{ margin:'12px 16px 0', padding:'12px', background:'#fffbeb', border:'1px solid #fde68a', borderRadius:'8px', fontSize:'13px', color:'#92400e' }}>
                    ⚠️ <strong>Already contacted</strong> on {dupWarning.date} ({dupWarning.days} days ago)
                    {dupWarning.followup && <span> · Follow-up: {new Date(dupWarning.followup).toLocaleDateString('en-IN')}</span>}
                  </div>
                )}

                <div style={{ padding:'16px' }}>
                  {/* Call Type */}
                  <div style={{ marginBottom:'14px' }}>
                    <label style={{ display:'block', fontSize:'11px', fontWeight:'700', color:'#475569', marginBottom:'6px', textTransform:'uppercase', letterSpacing:'0.5px' }}>Call Type</label>
                    <div style={{ display:'flex', gap:'8px' }}>
                      {[{v:'winback',l:'📞 Retention'},{v:'new_enquiry',l:'🆕 Enquiry'},{v:'welcome_call',l:'👋 Welcome'}].map(t => (
                        <button key={t.v} className={`outcome-btn${callForm.call_type===t.v?' sel':''}`} onClick={() => setCallForm({...callForm, call_type:t.v, outcome:''})} style={{ flex:1, textAlign:'center', fontSize:'12px' }}>{t.l}</button>
                      ))}
                    </div>
                  </div>

                  {/* Call Status */}
                  <div style={{ marginBottom:'14px' }}>
                    <label style={{ display:'block', fontSize:'11px', fontWeight:'700', color:'#475569', marginBottom:'6px', textTransform:'uppercase', letterSpacing:'0.5px' }}>Call Status *</label>
                    <select className="cinp" value={callForm.call_status} onChange={e => setCallForm({...callForm, call_status:e.target.value})}>
                      {CALL_STATUS.map(s => <option key={s} value={s}>{s.replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase())}</option>)}
                    </select>
                  </div>

                  {/* Outcome */}
                  <div style={{ marginBottom:'14px' }}>
                    <label style={{ display:'block', fontSize:'11px', fontWeight:'700', color:'#475569', marginBottom:'6px', textTransform:'uppercase', letterSpacing:'0.5px' }}>Outcome *</label>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'6px' }}>
                      {(OUTCOMES[callForm.call_type]||[]).map(o => (
                        <button key={o.value} className={`outcome-btn${callForm.outcome===o.value?' sel':''}`}
                          onClick={() => setCallForm({...callForm, outcome:o.value})} style={{ fontSize:'12px' }}>
                          {o.label}
                          {o.remove && <span style={{ fontSize:'10px', color:'#ef4444', display:'block' }}>removes from list</span>}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Sub-outcome: Switched ISP */}
                  {callForm.outcome==='switched_isp' && (
                    <div style={{ background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:'8px', padding:'12px', marginBottom:'14px' }}>
                      <div style={{ marginBottom:'10px' }}>
                        <label style={{ display:'block', fontSize:'11px', fontWeight:'700', color:'#15803d', marginBottom:'6px', textTransform:'uppercase' }}>Which ISP? *</label>
                        <select className="cinp" value={callForm.isp_name} onChange={e=>setCallForm({...callForm,isp_name:e.target.value})}>
                          <option value=''>-- Select ISP --</option>
                          {ISP_LIST.map(i=><option key={i} value={i}>{i}</option>)}
                        </select>
                      </div>
                      <div style={{ marginBottom:'10px' }}>
                        <label style={{ display:'block', fontSize:'11px', fontWeight:'700', color:'#15803d', marginBottom:'6px', textTransform:'uppercase' }}>Why did they switch? *</label>
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'6px' }}>
                          {SWITCH_REASONS.map(r=>(
                            <button key={r.value} className={`outcome-btn${callForm.switch_reason===r.value?' sel':''}`}
                              onClick={()=>setCallForm({...callForm,switch_reason:r.value,service_issue_type:'',lco_complaint_type:''})}
                              style={{fontSize:'12px'}}>{r.label}</button>
                          ))}
                        </div>
                      </div>
                      {callForm.switch_reason==='service_issue' && (
                        <div style={{ marginBottom:'10px' }}>
                          <label style={{ display:'block', fontSize:'11px', fontWeight:'700', color:'#15803d', marginBottom:'6px', textTransform:'uppercase' }}>Service Issue Type *</label>
                          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'6px' }}>
                            {SERVICE_ISSUES.map(s=>(
                              <button key={s.value} className={`outcome-btn${callForm.service_issue_type===s.value?' sel':''}`}
                                onClick={()=>setCallForm({...callForm,service_issue_type:s.value,lco_complaint_type:''})}
                                style={{fontSize:'12px'}}>{s.label}</button>
                            ))}
                          </div>
                        </div>
                      )}
                      {callForm.switch_reason==='service_issue' && callForm.service_issue_type==='lco_related' && (
                        <div style={{ marginBottom:'10px' }}>
                          <label style={{ display:'block', fontSize:'11px', fontWeight:'700', color:'#15803d', marginBottom:'6px', textTransform:'uppercase' }}>LCO Complaint Type *</label>
                          <div style={{ display:'grid', gridTemplateColumns:'1fr', gap:'6px' }}>
                            {LCO_COMPLAINTS.map(l=>(
                              <button key={l.value} className={`outcome-btn${callForm.lco_complaint_type===l.value?' sel':''}`}
                                onClick={()=>setCallForm({...callForm,lco_complaint_type:l.value})}
                                style={{fontSize:'12px'}}>{l.label}</button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Sub-outcome: Technical Issue */}
                  {callForm.outcome==='technical_issue' && (
                    <div style={{ background:'#f0f9ff', border:'1px solid #bae6fd', borderRadius:'8px', padding:'12px', marginBottom:'14px' }}>
                      <label style={{ display:'block', fontSize:'11px', fontWeight:'700', color:'#0369a1', marginBottom:'8px', textTransform:'uppercase' }}>Technical Issue Type *</label>
                      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'6px' }}>
                        {SERVICE_ISSUES.filter(s=>s.value!=='lco_related').map(s=>(
                          <button key={s.value} className={`outcome-btn${callForm.service_issue_type===s.value?' sel':''}`}
                            onClick={()=>setCallForm({...callForm,service_issue_type:s.value})}
                            style={{fontSize:'12px'}}>{s.label}</button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Sub-outcome: Financial Issue */}
                  {callForm.outcome==='no_money' && (
                    <div style={{ background:'#fefce8', border:'1px solid #fde68a', borderRadius:'8px', padding:'12px', marginBottom:'14px' }}>
                      <label style={{ display:'block', fontSize:'11px', fontWeight:'700', color:'#92400e', marginBottom:'8px', textTransform:'uppercase' }}>Financial Reason *</label>
                      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'6px' }}>
                        {FINANCIAL_REASONS.map(r=>(
                          <button key={r.value} className={`outcome-btn${callForm.financial_reason===r.value?' sel':''}`}
                            onClick={()=>setCallForm({...callForm,financial_reason:r.value})}
                            style={{fontSize:'12px'}}>{r.label}</button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Sub-outcome: LCO Issue */}
                  {callForm.outcome==='lco_issue' && (
                    <div style={{ background:'#fff7ed', border:'1px solid #fed7aa', borderRadius:'8px', padding:'12px', marginBottom:'14px' }}>
                      <label style={{ display:'block', fontSize:'11px', fontWeight:'700', color:'#c2410c', marginBottom:'8px', textTransform:'uppercase' }}>LCO Complaint Type *</label>
                      <div style={{ display:'grid', gridTemplateColumns:'1fr', gap:'6px' }}>
                        {LCO_COMPLAINTS.map(l=>(
                          <button key={l.value} className={`outcome-btn${callForm.lco_complaint_type===l.value?' sel':''}`}
                            onClick={()=>setCallForm({...callForm,lco_complaint_type:l.value})}
                            style={{fontSize:'12px'}}>{l.label}</button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Sub-outcome: Due to Studies */}
                  {callForm.outcome==='due_to_studies' && (
                    <div style={{ background:'#fdf4ff', border:'1px solid #e9d5ff', borderRadius:'8px', padding:'12px', marginBottom:'14px' }}>
                      <label style={{ display:'block', fontSize:'11px', fontWeight:'700', color:'#7e22ce', marginBottom:'8px', textTransform:'uppercase' }}>Study Reason *</label>
                      <div style={{ display:'grid', gridTemplateColumns:'1fr', gap:'6px' }}>
                        {STUDY_REASONS.map(r=>(
                          <button key={r.value} className={`outcome-btn${callForm.study_reason===r.value?' sel':''}`}
                            onClick={()=>setCallForm({...callForm,study_reason:r.value})}
                            style={{fontSize:'12px'}}>{r.label}</button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Sub-outcome: New KCCL Connection */}
                  {callForm.outcome==='new_kccl_connection' && (
                    <div style={{ background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:'8px', padding:'12px', marginBottom:'14px' }}>
                      <div style={{ marginBottom:'10px' }}>
                        <label style={{ display:'block', fontSize:'11px', fontWeight:'700', color:'#15803d', marginBottom:'8px', textTransform:'uppercase' }}>Reason for Re-connection *</label>
                        <div style={{ display:'grid', gridTemplateColumns:'1fr', gap:'6px' }}>
                          {KCCL_REASONS.map(r=>(
                            <button key={r.value} className={`outcome-btn${callForm.kccl_reason===r.value?' sel':''}`}
                              onClick={()=>setCallForm({...callForm,kccl_reason:r.value,kccl_returned_isp:'',kccl_return_reason:''})}
                              style={{fontSize:'12px'}}>{r.label}</button>
                          ))}
                        </div>
                      </div>
                      {callForm.kccl_reason==='returned_from_isp' && (
                        <>
                          <div style={{ marginBottom:'10px' }}>
                            <label style={{ display:'block', fontSize:'11px', fontWeight:'700', color:'#15803d', marginBottom:'6px', textTransform:'uppercase' }}>Which ISP did they try?</label>
                            <select className="cinp" value={callForm.kccl_returned_isp||''} onChange={e=>setCallForm({...callForm,kccl_returned_isp:e.target.value})}>
                              <option value=''>-- Select ISP --</option>
                              {ISP_LIST.filter(i=>i!=='Not Informed'&&i!=='Converted KCBL Postpaid').map(i=><option key={i} value={i}>{i}</option>)}
                            </select>
                          </div>
                          <div>
                            <label style={{ display:'block', fontSize:'11px', fontWeight:'700', color:'#15803d', marginBottom:'6px', textTransform:'uppercase' }}>Why did they come back?</label>
                            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'6px' }}>
                              {RETURN_REASONS.map(r=>(
                                <button key={r.value} className={`outcome-btn${callForm.kccl_return_reason===r.value?' sel':''}`}
                                  onClick={()=>setCallForm({...callForm,kccl_return_reason:r.value})}
                                  style={{fontSize:'12px'}}>{r.label}</button>
                              ))}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {/* Sub-outcome: Relocated */}
                  {callForm.outcome==='relocated' && (
                    <div style={{ background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:'8px', padding:'12px', marginBottom:'14px' }}>
                      <div style={{ marginBottom:'10px' }}>
                        <label style={{ display:'block', fontSize:'11px', fontWeight:'700', color:'#1d4ed8', marginBottom:'6px', textTransform:'uppercase' }}>Relocation Type *</label>
                        <div style={{ display:'grid', gridTemplateColumns:'1fr', gap:'6px' }}>
                          {RELOCATION_TYPES.map(r=>(
                            <button key={r.value} className={`outcome-btn${callForm.relocation_type===r.value?' sel':''}`}
                              onClick={()=>setCallForm({...callForm,relocation_type:r.value,relocation_location:'',relocation_district:'',relocation_pincode:''})}
                              style={{fontSize:'12px'}}>{r.label}</button>
                          ))}
                        </div>
                      </div>
                      {callForm.relocation_type==='no_network' && (
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px' }}>
                          <div>
                            <label style={{ display:'block', fontSize:'11px', fontWeight:'600', color:'#1d4ed8', marginBottom:'4px' }}>Location Name</label>
                            <input className="cinp" placeholder="Area/Place name" value={callForm.relocation_location} onChange={e=>setCallForm({...callForm,relocation_location:e.target.value})} />
                          </div>
                          <div>
                            <label style={{ display:'block', fontSize:'11px', fontWeight:'600', color:'#1d4ed8', marginBottom:'4px' }}>District</label>
                            <input className="cinp" placeholder="District" value={callForm.relocation_district} onChange={e=>setCallForm({...callForm,relocation_district:e.target.value})} />
                          </div>
                          <div>
                            <label style={{ display:'block', fontSize:'11px', fontWeight:'600', color:'#1d4ed8', marginBottom:'4px' }}>Pincode</label>
                            <input className="cinp" placeholder="Pincode" value={callForm.relocation_pincode} onChange={e=>setCallForm({...callForm,relocation_pincode:e.target.value})} />
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Sub-outcome: Temporary Connection */}
                  {callForm.outcome==='temporary_connection' && (
                    <div style={{ background:'#fff7ed', border:'1px solid #fed7aa', borderRadius:'8px', padding:'12px', marginBottom:'14px' }}>
                      <p style={{ margin:0, fontSize:'12px', color:'#c2410c', fontWeight:'600' }}>⏳ Please add remarks explaining the temporary connection duration/reason.</p>
                    </div>
                  )}

                  {/* Follow-up date */}
                  {needsFollowup && (
                    <div style={{ marginBottom:'14px' }}>
                      <label style={{ display:'block', fontSize:'11px', fontWeight:'700', color:'#475569', marginBottom:'6px', textTransform:'uppercase', letterSpacing:'0.5px' }}>Follow-up Date</label>
                      <input className="cinp" type="date" value={callForm.follow_up_date}
                        onChange={e => setCallForm({...callForm, follow_up_date:e.target.value})}
                        min={new Date().toISOString().split('T')[0]} />
                    </div>
                  )}

                  {/* Remarks */}
                  <div style={{ marginBottom:'16px' }}>
                    <label style={{ display:'block', fontSize:'11px', fontWeight:'700', color:'#475569', marginBottom:'6px', textTransform:'uppercase', letterSpacing:'0.5px' }}>Remarks</label>
                    <textarea className="cinp" rows={3} placeholder="Notes about this call..." style={{ resize:'vertical' }}
                      value={callForm.remarks} onChange={e => setCallForm({...callForm, remarks:e.target.value})} />
                  </div>

                  {submitMsg && (
                    <div style={{ padding:'10px 14px', borderRadius:'8px', marginBottom:'12px', fontSize:'13px', background: submitMsg.type==='success'?'#ecfdf5':'#fef2f2', color: submitMsg.type==='success'?'#059669':'#dc2626', border:`1px solid ${submitMsg.type==='success'?'#a7f3d0':'#fecaca'}` }}>
                      {submitMsg.type==='success'?'✅':'⚠️'} {submitMsg.text}
                    </div>
                  )}

                  <button className="sbtn" onClick={submitCall} disabled={submitting} style={{ width:'100%' }}>
                    {submitting ? '⏳ Saving...' : '✓ Save Call Log'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* CALL TAB - Quick log without lead selection */}
      {tab === 'call' && (
        <div style={{ maxWidth:'700px' }}>
          <div style={{ background:'white', borderRadius:'14px', padding:'28px', boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
            <h3 style={{ margin:'0 0 20px', fontSize:'16px', fontWeight:'700', color:'#0f172a' }}>📞 Log a Call</h3>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px', marginBottom:'14px' }}>
              <div>
                <label style={{ display:'block', fontSize:'11px', fontWeight:'700', color:'#475569', marginBottom:'6px', textTransform:'uppercase', letterSpacing:'0.5px' }}>User ID *</label>
                <input className="cinp" placeholder="e.g. arshad5702" value={callForm.customer_user_id||''}
                  onChange={async e => {
                    const uid = e.target.value;
                    setCallForm(f=>({...f, customer_user_id:uid}));
                    if (uid.length >= 3) {
                      // Search in retention leads first
                      try {
                        const res = await axios.get(`${API}/retention/search?q=${uid}&limit=1`, { headers });
                        // Only use lead data if user_id exactly matches
                        const exactLead = res.data.leads.find(l => l.user_id === uid);
                        const lead = exactLead || null;
                        if (lead) {
                          setCallForm(f=>({...f, customer_user_id:uid, customer_name:lead.name||f.customer_name, customer_mobile:lead.mobile||f.customer_mobile, customer_plan:lead.plan||f.customer_plan, customer_area:lead.sub_dist_code, customer_lco:lead.lco_name, customer_sub_dist:lead.sub_dist_code, customer_lco_code:lead.lco_code}));
                          if (lead.last_contacted) {
                            const days = Math.floor((new Date()-new Date(lead.last_contacted))/86400000);
                            if (days < 7) setDupWarning({ date:new Date(lead.last_contacted).toLocaleDateString('en-IN'), days, followup:lead.follow_up_date });
                          }
                          return;
                        }
                      } catch(e) {}
                      // Fallback to BMS only if not in retention list
                      setBmsLoading(true);
                      try {
                        const res = await axios.get(`${API}/bms-lookup/${uid}`, { headers, timeout:35000 });
                        if (res.data.success && res.data.session) {
                          const s = res.data.session;
                          setBmsData(s);
                          setCallForm(f=>({...f,
                            customer_user_id: uid,
                            customer_name: f.customer_name || s.name,
                            customer_mobile: f.customer_mobile || s.mobile,
                            customer_plan: f.customer_plan || s.plan
                          }));
                        }
                      } catch(e) {}
                      setBmsLoading(false);
                    }
                  }} />
                {bmsLoading && <p style={{ margin:'4px 0 0', fontSize:'11px', color:'#f59e0b' }}>⏳ Searching with ISP...</p>}
              </div>
              <div>
                <label style={{ display:'block', fontSize:'11px', fontWeight:'700', color:'#475569', marginBottom:'6px', textTransform:'uppercase', letterSpacing:'0.5px' }}>Customer Name</label>
                <input className="cinp" placeholder="Auto-filled" value={callForm.customer_name||''} onChange={e => setCallForm(f=>({...f, customer_name:e.target.value}))} />
              </div>
              <div>
                <label style={{ display:'block', fontSize:'11px', fontWeight:'700', color:'#475569', marginBottom:'6px', textTransform:'uppercase', letterSpacing:'0.5px' }}>Mobile</label>
                <input className="cinp" placeholder="Auto-filled" value={callForm.customer_mobile||''} onChange={e => setCallForm(f=>({...f, customer_mobile:e.target.value}))} />
              </div>
              <div>
                <label style={{ display:'block', fontSize:'11px', fontWeight:'700', color:'#475569', marginBottom:'6px', textTransform:'uppercase', letterSpacing:'0.5px' }}>Plan</label>
                <input className="cinp" placeholder="Auto-filled" value={callForm.customer_plan||''} onChange={e => setCallForm(f=>({...f, customer_plan:e.target.value}))} />
              </div>
              <div>
                <label style={{ display:'block', fontSize:'11px', fontWeight:'700', color:'#475569', marginBottom:'6px', textTransform:'uppercase', letterSpacing:'0.5px' }}>LCO Name</label>
                <input className="cinp" placeholder="Auto-filled" value={callForm.customer_lco||''} onChange={e => setCallForm(f=>({...f, customer_lco:e.target.value}))} />
              </div>
              <div>
                <label style={{ display:'block', fontSize:'11px', fontWeight:'700', color:'#475569', marginBottom:'6px', textTransform:'uppercase', letterSpacing:'0.5px' }}>LCO Code</label>
                <input className="cinp" placeholder="Auto-filled" value={callForm.customer_lco_code||''} onChange={e => setCallForm(f=>({...f, customer_lco_code:e.target.value}))} />
              </div>
              <div>
                <label style={{ display:'block', fontSize:'11px', fontWeight:'700', color:'#475569', marginBottom:'6px', textTransform:'uppercase', letterSpacing:'0.5px' }}>Sub-Dist Code</label>
                <input className="cinp" placeholder="Auto-filled" value={callForm.customer_sub_dist||''} onChange={e => setCallForm(f=>({...f, customer_sub_dist:e.target.value}))} />
              </div>
            </div>

            {dupWarning && (
              <div style={{ padding:'12px', background:'#fffbeb', border:'1px solid #fde68a', borderRadius:'8px', fontSize:'13px', color:'#92400e', marginBottom:'14px' }}>
                ⚠️ <strong>Already contacted</strong> on {dupWarning.date} ({dupWarning.days} days ago)
                {dupWarning.followup && <span> · Follow-up: {new Date(dupWarning.followup).toLocaleDateString('en-IN')}</span>}
              </div>
            )}

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px', marginBottom:'14px' }}>
              <div>
                <label style={{ display:'block', fontSize:'11px', fontWeight:'700', color:'#475569', marginBottom:'6px', textTransform:'uppercase', letterSpacing:'0.5px' }}>Call Type</label>
                <select className="cinp" value={callForm.call_type||'winback'} onChange={e => setCallForm(f=>({...f, call_type:e.target.value, outcome:''}))}>
                  <option value="winback">📞 Retention Call</option>
                  <option value="new_enquiry">🆕 New Enquiry</option>
                  <option value="welcome_call">👋 Welcome Call</option>
                </select>
              </div>
              <div>
                <label style={{ display:'block', fontSize:'11px', fontWeight:'700', color:'#475569', marginBottom:'6px', textTransform:'uppercase', letterSpacing:'0.5px' }}>Call Status</label>
                <select className="cinp" value={callForm.call_status||'answered'} onChange={e => setCallForm(f=>({...f, call_status:e.target.value}))}>
                  {CALL_STATUS.map(s => <option key={s} value={s}>{s.replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase())}</option>)}
                </select>
              </div>
            </div>

            <div style={{ marginBottom:'14px' }}>
              <label style={{ display:'block', fontSize:'11px', fontWeight:'700', color:'#475569', marginBottom:'6px', textTransform:'uppercase', letterSpacing:'0.5px' }}>Outcome</label>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'6px' }}>
                {(OUTCOMES[callForm.call_type||'winback']||[]).map(o => (
                  <button key={o.value} className={`outcome-btn${callForm.outcome===o.value?' sel':''}`}
                    onClick={() => setCallForm(f=>({...f, outcome:o.value}))} style={{ fontSize:'12px' }}>
                    {o.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Sub-outcome: Switched ISP */}
            {callForm.outcome==='switched_isp' && (
              <div style={{ background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:'8px', padding:'12px', marginBottom:'14px' }}>
                <div style={{ marginBottom:'10px' }}>
                  <label style={{ display:'block', fontSize:'11px', fontWeight:'700', color:'#15803d', marginBottom:'6px', textTransform:'uppercase' }}>Which ISP? *</label>
                  <select className="cinp" value={callForm.isp_name||''} onChange={e=>setCallForm(f=>({...f,isp_name:e.target.value}))}>
                    <option value=''>-- Select ISP --</option>
                    {ISP_LIST.map(i=><option key={i} value={i}>{i}</option>)}
                  </select>
                </div>
                <div style={{ marginBottom:'10px' }}>
                  <label style={{ display:'block', fontSize:'11px', fontWeight:'700', color:'#15803d', marginBottom:'6px', textTransform:'uppercase' }}>Why did they switch? *</label>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'6px' }}>
                    {SWITCH_REASONS.map(r=>(<button key={r.value} className={`outcome-btn${callForm.switch_reason===r.value?' sel':''}`} onClick={()=>setCallForm(f=>({...f,switch_reason:r.value,service_issue_type:'',lco_complaint_type:''}))} style={{fontSize:'12px'}}>{r.label}</button>))}
                  </div>
                </div>
                {callForm.switch_reason==='service_issue' && (<div style={{ marginBottom:'10px' }}><label style={{ display:'block', fontSize:'11px', fontWeight:'700', color:'#15803d', marginBottom:'6px', textTransform:'uppercase' }}>Service Issue Type *</label><div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'6px' }}>{SERVICE_ISSUES.map(s=>(<button key={s.value} className={`outcome-btn${callForm.service_issue_type===s.value?' sel':''}`} onClick={()=>setCallForm(f=>({...f,service_issue_type:s.value,lco_complaint_type:''}))} style={{fontSize:'12px'}}>{s.label}</button>))}</div></div>)}
                {callForm.switch_reason==='service_issue'&&callForm.service_issue_type==='lco_related' && (<div style={{ marginBottom:'10px' }}><label style={{ display:'block', fontSize:'11px', fontWeight:'700', color:'#15803d', marginBottom:'6px', textTransform:'uppercase' }}>LCO Complaint *</label><div style={{ display:'grid', gridTemplateColumns:'1fr', gap:'6px' }}>{LCO_COMPLAINTS.map(l=>(<button key={l.value} className={`outcome-btn${callForm.lco_complaint_type===l.value?' sel':''}`} onClick={()=>setCallForm(f=>({...f,lco_complaint_type:l.value}))} style={{fontSize:'12px'}}>{l.label}</button>))}</div></div>)}
              </div>
            )}
            {callForm.outcome==='technical_issue' && (
              <div style={{ background:'#f0f9ff', border:'1px solid #bae6fd', borderRadius:'8px', padding:'12px', marginBottom:'14px' }}>
                <label style={{ display:'block', fontSize:'11px', fontWeight:'700', color:'#0369a1', marginBottom:'8px', textTransform:'uppercase' }}>Technical Issue Type *</label>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'6px' }}>
                  {SERVICE_ISSUES.filter(s=>s.value!=='lco_related').map(s=>(
                    <button key={s.value} className={`outcome-btn${callForm.service_issue_type===s.value?' sel':''}`}
                      onClick={()=>setCallForm(f=>({...f,service_issue_type:s.value}))}
                      style={{fontSize:'12px'}}>{s.label}</button>
                  ))}
                </div>
              </div>
            )}
            {callForm.outcome==='no_money' && (
              <div style={{ background:'#fefce8', border:'1px solid #fde68a', borderRadius:'8px', padding:'12px', marginBottom:'14px' }}>
                <label style={{ display:'block', fontSize:'11px', fontWeight:'700', color:'#92400e', marginBottom:'8px', textTransform:'uppercase' }}>Financial Reason *</label>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'6px' }}>
                  {FINANCIAL_REASONS.map(r=>(
                    <button key={r.value} className={`outcome-btn${callForm.financial_reason===r.value?' sel':''}`}
                      onClick={()=>setCallForm(f=>({...f,financial_reason:r.value}))}
                      style={{fontSize:'12px'}}>{r.label}</button>
                  ))}
                </div>
              </div>
            )}
            {callForm.outcome==='lco_issue' && (
              <div style={{ background:'#fff7ed', border:'1px solid #fed7aa', borderRadius:'8px', padding:'12px', marginBottom:'14px' }}>
                <label style={{ display:'block', fontSize:'11px', fontWeight:'700', color:'#c2410c', marginBottom:'8px', textTransform:'uppercase' }}>LCO Complaint Type *</label>
                <div style={{ display:'grid', gridTemplateColumns:'1fr', gap:'6px' }}>
                  {LCO_COMPLAINTS.map(l=>(
                    <button key={l.value} className={`outcome-btn${callForm.lco_complaint_type===l.value?' sel':''}`}
                      onClick={()=>setCallForm(f=>({...f,lco_complaint_type:l.value}))}
                      style={{fontSize:'12px'}}>{l.label}</button>
                  ))}
                </div>
              </div>
            )}
            {callForm.outcome==='due_to_studies' && (
              <div style={{ background:'#fdf4ff', border:'1px solid #e9d5ff', borderRadius:'8px', padding:'12px', marginBottom:'14px' }}>
                <label style={{ display:'block', fontSize:'11px', fontWeight:'700', color:'#7e22ce', marginBottom:'8px', textTransform:'uppercase' }}>Study Reason *</label>
                <div style={{ display:'grid', gridTemplateColumns:'1fr', gap:'6px' }}>
                  {STUDY_REASONS.map(r=>(
                    <button key={r.value} className={`outcome-btn${callForm.study_reason===r.value?' sel':''}`}
                      onClick={()=>setCallForm(f=>({...f,study_reason:r.value}))}
                      style={{fontSize:'12px'}}>{r.label}</button>
                  ))}
                </div>
              </div>
            )}
            {callForm.outcome==='new_kccl_connection' && (
              <div style={{ background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:'8px', padding:'12px', marginBottom:'14px' }}>
                <div style={{ marginBottom:'10px' }}>
                  <label style={{ display:'block', fontSize:'11px', fontWeight:'700', color:'#15803d', marginBottom:'8px', textTransform:'uppercase' }}>Reason for Re-connection *</label>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr', gap:'6px' }}>
                    {KCCL_REASONS.map(r=>(
                      <button key={r.value} className={`outcome-btn${callForm.kccl_reason===r.value?' sel':''}`}
                        onClick={()=>setCallForm(f=>({...f,kccl_reason:r.value,kccl_returned_isp:'',kccl_return_reason:''}))}
                        style={{fontSize:'12px'}}>{r.label}</button>
                    ))}
                  </div>
                </div>
                {callForm.kccl_reason==='returned_from_isp' && (<>
                  <div style={{ marginBottom:'10px' }}>
                    <label style={{ display:'block', fontSize:'11px', fontWeight:'700', color:'#15803d', marginBottom:'6px', textTransform:'uppercase' }}>Which ISP did they try?</label>
                    <select className="cinp" value={callForm.kccl_returned_isp||''} onChange={e=>setCallForm(f=>({...f,kccl_returned_isp:e.target.value}))}>
                      <option value=''>-- Select ISP --</option>
                      {ISP_LIST.filter(i=>i!=='Not Informed'&&i!=='Converted KCBL Postpaid').map(i=><option key={i} value={i}>{i}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ display:'block', fontSize:'11px', fontWeight:'700', color:'#15803d', marginBottom:'6px', textTransform:'uppercase' }}>Why did they come back?</label>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'6px' }}>
                      {RETURN_REASONS.map(r=>(
                        <button key={r.value} className={`outcome-btn${callForm.kccl_return_reason===r.value?' sel':''}`}
                          onClick={()=>setCallForm(f=>({...f,kccl_return_reason:r.value}))}
                          style={{fontSize:'12px'}}>{r.label}</button>
                      ))}
                    </div>
                  </div>
                </>)}
              </div>
            )}
            {callForm.outcome==='relocated' && (
              <div style={{ background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:'8px', padding:'12px', marginBottom:'14px' }}>
                <div style={{ marginBottom:'10px' }}>
                  <label style={{ display:'block', fontSize:'11px', fontWeight:'700', color:'#1d4ed8', marginBottom:'6px', textTransform:'uppercase' }}>Relocation Type *</label>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr', gap:'6px' }}>
                    {RELOCATION_TYPES.map(r=>(<button key={r.value} className={`outcome-btn${callForm.relocation_type===r.value?' sel':''}`} onClick={()=>setCallForm(f=>({...f,relocation_type:r.value,relocation_location:'',relocation_district:'',relocation_pincode:''}))} style={{fontSize:'12px'}}>{r.label}</button>))}
                  </div>
                </div>
                {callForm.relocation_type==='no_network' && (<div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px' }}><div><label style={{ fontSize:'11px', fontWeight:'600', color:'#1d4ed8', display:'block', marginBottom:'4px' }}>Location</label><input className="cinp" placeholder="Area/Place" value={callForm.relocation_location||''} onChange={e=>setCallForm(f=>({...f,relocation_location:e.target.value}))} /></div><div><label style={{ fontSize:'11px', fontWeight:'600', color:'#1d4ed8', display:'block', marginBottom:'4px' }}>District</label><input className="cinp" placeholder="District" value={callForm.relocation_district||''} onChange={e=>setCallForm(f=>({...f,relocation_district:e.target.value}))} /></div><div><label style={{ fontSize:'11px', fontWeight:'600', color:'#1d4ed8', display:'block', marginBottom:'4px' }}>Pincode</label><input className="cinp" placeholder="Pincode" value={callForm.relocation_pincode||''} onChange={e=>setCallForm(f=>({...f,relocation_pincode:e.target.value}))} /></div></div>)}
              </div>
            )}
            {callForm.outcome==='temporary_connection' && (<div style={{ background:'#fff7ed', border:'1px solid #fed7aa', borderRadius:'8px', padding:'12px', marginBottom:'14px' }}><p style={{ margin:0, fontSize:'12px', color:'#c2410c', fontWeight:'600' }}>⏳ Please add remarks about the temporary connection duration/reason.</p></div>)}
            {needsFollowup && (
              <div style={{ marginBottom:'14px' }}>
                <label style={{ display:'block', fontSize:'11px', fontWeight:'700', color:'#475569', marginBottom:'6px', textTransform:'uppercase', letterSpacing:'0.5px' }}>Follow-up Date</label>
                <input className="cinp" type="date" value={callForm.follow_up_date||''} min={new Date().toISOString().split('T')[0]}
                  onChange={e => setCallForm(f=>({...f, follow_up_date:e.target.value}))} />
              </div>
            )}

            <div style={{ marginBottom:'20px' }}>
              <label style={{ display:'block', fontSize:'11px', fontWeight:'700', color:'#475569', marginBottom:'6px', textTransform:'uppercase', letterSpacing:'0.5px' }}>Remarks</label>
              <textarea className="cinp" rows={3} placeholder="Notes about this call..." style={{ resize:'vertical' }}
                value={callForm.remarks||''} onChange={e => setCallForm(f=>({...f, remarks:e.target.value}))} />
            </div>

            {submitMsg && (
              <div style={{ padding:'10px 14px', borderRadius:'8px', marginBottom:'12px', fontSize:'13px', background:submitMsg.type==='success'?'#ecfdf5':'#fef2f2', color:submitMsg.type==='success'?'#059669':'#dc2626', border:`1px solid ${submitMsg.type==='success'?'#a7f3d0':'#fecaca'}` }}>
                {submitMsg.type==='success'?'✅':'⚠️'} {submitMsg.text}
              </div>
            )}

            <button className="sbtn" onClick={async () => {
              if (!callForm.customer_user_id || !callForm.call_status) { setSubmitMsg({type:'error',text:'User ID and call status required'}); return; }
              setSubmitting(true);
              console.log('[CRM Save] Sending:', JSON.stringify(callForm));
              try {
                await axios.post(`${API}/log`, callForm, { headers });
                if (callForm.customer_user_id && callForm.outcome) {
                  axios.put(`${API}/retention/${callForm.customer_user_id}`, callForm, { headers }).catch(()=>{});
                }
                setSubmitMsg({type:'success',text:'Call logged successfully!'});
                setCallForm({ call_type:'winback', call_status:'answered', outcome:'', follow_up_date:'', remarks:'', isp_name:'', switch_reason:'', service_issue_type:'', lco_complaint_type:'', relocation_type:'', relocation_location:'', relocation_district:'', relocation_pincode:'', financial_reason:'', kccl_reason:'', kccl_returned_isp:'', kccl_return_reason:'', study_reason:'', customer_user_id:'', customer_name:'', customer_mobile:'', customer_plan:'' });
                setBmsData(null); setDupWarning(null);
                if (isManagement) loadStats();
              } catch(e) { setSubmitMsg({type:'error',text:'Failed to save'}); }
              setSubmitting(false);
            }} disabled={submitting} style={{ width:'100%' }}>
              {submitting ? '⏳ Saving...' : '✓ Save Call Log'}
            </button>
          </div>
        </div>
      )}

      {/* FOLLOW-UPS TAB */}
      {tab === 'followups' && (
        <div style={{ background:'white', borderRadius:'14px', boxShadow:'0 1px 4px rgba(0,0,0,0.06)', overflow:'hidden' }}>
          <div style={{ padding:'20px', borderBottom:'1px solid #f1f5f9' }}>
            <h3 style={{ margin:0, fontSize:'15px', fontWeight:'700', color:'#0f172a' }}>🔔 Follow-ups Due Today ({followups.length})</h3>
          </div>
          {followups.length === 0 ? (
            <div style={{ padding:'60px', textAlign:'center', color:'#94a3b8' }}>
              <div style={{ fontSize:'48px', marginBottom:'8px' }}>🎉</div>
              <p>No follow-ups due today!</p>
            </div>
          ) : (
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead>
                <tr style={{ background:'#f8fafc' }}>
                  {['Name','User ID','Mobile','Sub-Dist','Plan','Last Outcome','Follow-up','Action'].map(h => (
                    <th key={h} style={{ padding:'12px', textAlign:'left', fontSize:'12px', color:'#64748b', fontWeight:'700', borderBottom:'2px solid #e2e8f0' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {followups.map((lead,i) => (
                  <tr key={i} style={{ borderBottom:'1px solid #f1f5f9' }}>
                    <td style={{ padding:'12px', fontSize:'13px', fontWeight:'600', color:'#1e293b' }}>{lead.name}</td>
                    <td style={{ padding:'12px', fontSize:'13px', color:'#6366f1', fontWeight:'600' }}>{lead.user_id}</td>
                    <td style={{ padding:'12px', fontSize:'13px', color:'#475569' }}>{lead.mobile}</td>
                    <td style={{ padding:'12px' }}><span style={{ background:`${primary}15`, color:primary, padding:'2px 8px', borderRadius:'10px', fontSize:'11px', fontWeight:'700' }}>{lead.sub_dist_code}</span></td>
                    <td style={{ padding:'12px', fontSize:'12px', color:'#64748b' }}>{lead.plan?.substring(0,15)}</td>
                    <td style={{ padding:'12px', fontSize:'12px', color:'#475569' }}>{lead.outcome?.replace(/_/g,' ')}</td>
                    <td style={{ padding:'12px', fontSize:'13px', color:'#f59e0b', fontWeight:'600' }}>{lead.follow_up_date ? new Date(lead.follow_up_date).toLocaleDateString('en-IN') : '—'}</td>
                    <td style={{ padding:'12px' }}>
                      <button onClick={() => { setTab('leads'); setSearchQ(lead.user_id); searchLeads(lead.user_id, searchFilter, 1); setTimeout(() => selectLead(lead), 500); }}
                        style={{ padding:'6px 12px', background:primary, color:'white', border:'none', borderRadius:'6px', fontSize:'12px', cursor:'pointer', fontWeight:'600' }}>
                        📞 Call
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* HISTORY TAB */}
      {tab === 'history' && (
        <div>
          <div style={{ background:'white', borderRadius:'14px', padding:'16px', marginBottom:'16px', boxShadow:'0 1px 4px rgba(0,0,0,0.06)', display:'flex', gap:'10px', flexWrap:'wrap' }}>
            <input className="cinp" style={{ width:'150px' }} type="date" value={logFilters.from} onChange={e => setLogFilters(f=>({...f, from:e.target.value}))} />
            <input className="cinp" style={{ width:'150px' }} type="date" value={logFilters.to} onChange={e => setLogFilters(f=>({...f, to:e.target.value}))} />
            {isManagement && <select className="cinp" style={{ width:'160px' }} value={logFilters.staff_id||''} onChange={e => setLogFilters(f=>({...f, staff_id:e.target.value}))}>
              <option value="">All Staff</option>
              {(stats?.crm?.byStaff||[]).map((s,i) => <option key={i} value={s._id.staff_id}>{s._id.staff_name}</option>)}
            </select>}
            <select className="cinp" style={{ width:'150px' }} value={logFilters.call_type||''} onChange={e => setLogFilters(f=>({...f, call_type:e.target.value}))}>
              <option value="">All Types</option>
              <option value="winback">Retention</option>
              <option value="new_enquiry">Enquiry</option>
              <option value="welcome_call">Welcome</option>
            </select>
            <select className="cinp" style={{ width:'180px' }} value={logFilters.outcome||''} onChange={e => setLogFilters(f=>({...f, outcome:e.target.value}))}>
              <option value="">All Outcomes</option>
              <option value="reactivated">✅ Reactivated</option>
              <option value="will_recharge_soon">🕐 Will Recharge Soon</option>
              <option value="callback_requested">📞 Callback Requested</option>
              <option value="technical_issue">🔧 Technical Issue</option>
              <option value="lco_issue">⚠️ LCO Issue</option>
              <option value="no_money">💸 Financial / Price Issue</option>
              <option value="out_of_station">✈️ Out of Station</option>
              <option value="permanently_left">❌ Permanently Left</option>
              <option value="switched_isp">🔄 Switched ISP</option>
              <option value="new_kccl_connection">🆕 New KCCL Connection</option>
              <option value="relocated">🏠 Relocated</option>
              <option value="temporary_connection">⏳ Temporary Connection</option>
              <option value="due_to_studies">📚 Due to Studies</option>
              <option value="not_applicable">📵 Not Reachable</option>
            </select>
            <button onClick={() => { setLogsPage(1); loadLogs(); }} style={{ padding:'10px 20px', background:primary, color:'white', border:'none', borderRadius:'8px', cursor:'pointer', fontWeight:'600' }}>Apply</button>
            <button onClick={() => { setLogFilters({from:'',to:'',staff_id:'',call_type:'',outcome:''}); setLogsPage(1); }} style={{ padding:'10px 16px', background:'#f1f5f9', color:'#475569', border:'1px solid #e2e8f0', borderRadius:'8px', cursor:'pointer' }}>Clear</button>
          </div>
          <div style={{ background:'white', borderRadius:'14px', boxShadow:'0 1px 4px rgba(0,0,0,0.06)', overflow:'hidden' }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead>
                <tr style={{ background:'#f8fafc' }}>
                  {['Date','Agent','Customer','User ID','Mobile','Sub-Dist','LCO','Type','Status','Outcome','Remarks'].map(h => (
                    <th key={h} style={{ padding:'12px', textAlign:'left', fontSize:'12px', color:'#64748b', fontWeight:'700', borderBottom:'2px solid #e2e8f0' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 ? (
                  <tr><td colSpan={9} style={{ padding:'40px', textAlign:'center', color:'#94a3b8' }}>No logs found</td></tr>
                ) : logs.map((log,i) => (
                  <tr key={i} style={{ borderBottom:'1px solid #f1f5f9' }}
                    onMouseEnter={e=>e.currentTarget.style.background='#f8fafc'}
                    onMouseLeave={e=>e.currentTarget.style.background='white'}>
                    <td style={{ padding:'12px', fontSize:'12px', color:'#64748b' }}>{new Date(log.createdAt).toLocaleDateString('en-IN')}<br/><span style={{ fontSize:'11px', color:'#94a3b8' }}>{new Date(log.createdAt).toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit'})}</span></td>
                    <td style={{ padding:'12px', fontSize:'13px', fontWeight:'600', color:'#1e293b' }}>{log.staff_name||'—'}</td>
                    <td style={{ padding:'12px', fontSize:'13px', color:'#1e293b' }}>{log.customer_name}</td>
                    <td style={{ padding:'12px', fontSize:'12px', color:'#6366f1', fontWeight:'600' }}>{log.customer_user_id}</td>
                    <td style={{ padding:'12px', fontSize:'12px', color:'#475569' }}>{log.customer_mobile}</td>
                    <td style={{ padding:'12px' }}><span style={{ background:`${primary}15`, color:primary, padding:'2px 8px', borderRadius:'10px', fontSize:'11px', fontWeight:'700' }}>{log.customer_sub_dist||log.customer_area||'—'}</span></td>
                    <td style={{ padding:'12px', fontSize:'12px', color:'#475569' }}>{log.customer_lco||'—'}</td>
                    <td style={{ padding:'12px', fontSize:'12px' }}><span style={{ background:'#f1f5f9', padding:'2px 8px', borderRadius:'10px' }}>{log.call_type?.replace(/_/g,' ')}</span></td>
                    <td style={{ padding:'12px', fontSize:'12px', color:'#475569' }}>{log.call_status?.replace(/_/g,' ')}</td>
                    <td style={{ padding:'12px', fontSize:'12px', color:primary, fontWeight:'500' }}>{log.outcome?.replace(/_/g,' ')}</td>
                    <td style={{ padding:'12px', fontSize:'12px', color:'#64748b', maxWidth:'150px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{log.remarks||'—'}</td>
                    <td style={{ padding:'8px 12px' }}>
                      {new Date(log.createdAt).toDateString()===new Date().toDateString() && (
                        <button onClick={()=>{
                          setEditingLog(log);
                          setEditLogForm({
                            call_status:log.call_status||'',
                            outcome:log.outcome||'',
                            remarks:log.remarks||'',
                            follow_up_date:log.follow_up_date?log.follow_up_date.split('T')[0]:'',
                          });
                        }} style={{ padding:'4px 10px', background:'#f1f5f9', border:'1px solid #e2e8f0', borderRadius:'6px', cursor:'pointer', fontSize:'11px', fontWeight:'600', color:'#475569' }}>✏️ Edit</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ padding:'12px 16px', borderTop:'1px solid #f1f5f9', display:'flex', justifyContent:'space-between' }}>
              <button disabled={logsPage===1} onClick={()=>setLogsPage(p=>p-1)} style={{ padding:'6px 14px', border:'1px solid #e2e8f0', borderRadius:'6px', cursor:'pointer' }}>← Prev</button>
              <span style={{ fontSize:'12px', color:'#64748b' }}>Page {logsPage} · {totalLogs} total</span>
              <button onClick={()=>setLogsPage(p=>p+1)} style={{ padding:'6px 14px', border:'1px solid #e2e8f0', borderRadius:'6px', cursor:'pointer' }}>Next →</button>
            </div>
          </div>
        </div>
      )}
          {editingLog && (
            <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center',padding:'20px'}}>
              <div style={{background:'white',borderRadius:'16px',width:'100%',maxWidth:'500px',padding:'24px',boxShadow:'0 20px 60px rgba(0,0,0,0.3)'}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'20px'}}>
                  <div style={{fontSize:'16px',fontWeight:'700',color:'#0f172a'}}>✏️ Edit Log — {editingLog.customer_name}</div>
                  <button onClick={()=>setEditingLog(null)} style={{padding:'6px 12px',background:'#f1f5f9',border:'1px solid #e2e8f0',borderRadius:'6px',cursor:'pointer'}}>✕</button>
                </div>
                <div style={{marginBottom:'12px'}}>
                  <label style={{display:'block',fontSize:'11px',fontWeight:'700',color:'#475569',marginBottom:'4px'}}>CALL STATUS</label>
                  <select className="cinp" style={{width:'100%'}} value={editLogForm.call_status} onChange={e=>setEditLogForm(f=>({...f,call_status:e.target.value}))}>
                    <option value="answered">Answered</option>
                    <option value="not_reachable">Not Reachable</option>
                    <option value="wrong_number">Wrong Number</option>
                    <option value="callback_requested">Callback Requested</option>
                    <option value="busy">Busy</option>
                  </select>
                </div>
                <div style={{marginBottom:'12px'}}>
                  <label style={{display:'block',fontSize:'11px',fontWeight:'700',color:'#475569',marginBottom:'4px'}}>OUTCOME</label>
                  <select className="cinp" style={{width:'100%'}} value={editLogForm.outcome} onChange={e=>setEditLogForm(f=>({...f,outcome:e.target.value}))}>
                    <option value="">Select Outcome</option>
                    <option value="reactivated">✅ Reactivated</option>
                    <option value="will_recharge_soon">🕐 Will Recharge Soon</option>
                    <option value="callback_requested">📞 Callback Requested</option>
                    <option value="technical_issue">🔧 Technical Issue</option>
                    <option value="lco_issue">⚠️ LCO Issue</option>
                    <option value="no_money">💸 Financial / Price Issue</option>
                    <option value="out_of_station">✈️ Out of Station</option>
                    <option value="permanently_left">❌ Permanently Left</option>
                    <option value="switched_isp">🔄 Switched ISP</option>
                    <option value="new_kccl_connection">🆕 New KCCL Connection</option>
                    <option value="relocated">🏠 Relocated</option>
                    <option value="temporary_connection">⏳ Temporary Connection</option>
                    <option value="due_to_studies">📚 Due to Studies</option>
                    <option value="not_applicable">📵 Not Reachable</option>
                  </select>
                </div>
                <div style={{marginBottom:'12px'}}>
                  <label style={{display:'block',fontSize:'11px',fontWeight:'700',color:'#475569',marginBottom:'4px'}}>REMARKS</label>
                  <textarea className="cinp" style={{width:'100%',height:'80px',resize:'vertical'}} value={editLogForm.remarks} onChange={e=>setEditLogForm(f=>({...f,remarks:e.target.value}))} />
                </div>
                <div style={{marginBottom:'20px'}}>
                  <label style={{display:'block',fontSize:'11px',fontWeight:'700',color:'#475569',marginBottom:'4px'}}>FOLLOW UP DATE</label>
                  <input type="date" className="cinp" style={{width:'100%'}} value={editLogForm.follow_up_date} onChange={e=>setEditLogForm(f=>({...f,follow_up_date:e.target.value}))} />
                </div>
                <div style={{display:'flex',gap:'10px',justifyContent:'flex-end'}}>
                  <button onClick={()=>setEditingLog(null)} style={{padding:'10px 20px',background:'#f1f5f9',border:'1px solid #e2e8f0',borderRadius:'8px',cursor:'pointer',fontWeight:'600'}}>Cancel</button>
                  <button onClick={async()=>{
                    try{
                      await axios.put(`${API}/log/${editingLog._id}`,editLogForm,{headers});
                      setEditingLog(null);
                      loadLogs();
                    }catch(e){alert('Failed to update');}
                  }} style={{padding:'10px 24px',background:'#1B6B3A',color:'white',border:'none',borderRadius:'8px',cursor:'pointer',fontWeight:'700'}}>Save Changes</button>
                </div>
              </div>
            </div>
          )}
    </div>
  );
}
